import { useLocalization } from "@progress/kendo-react-intl";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AccessToken } from "../config/AccessToken";
import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import {
  TransformWrapper,
  TransformComponent,
  useControls
} from "react-zoom-pan-pinch";
import { Checkbox, Switch } from "@progress/kendo-react-inputs";

import { AzureMapComponent } from "./AzureMapComponent";
import { useNavigate } from "react-router-dom";
import { Avatar, ExpansionPanel, ExpansionPanelContent } from "@progress/kendo-react-layout";
import { SvgIcon } from "@progress/kendo-react-common";
import { trashIcon } from "@progress/kendo-svg-icons";
import { NotificationGroup, Notification } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import { currentLocation } from "../assets/current-location";
import { loadingPanel } from "../components/LoadingPanel";
import { getSharedMqttClient, isMqttConnectionEnabled, parseMqttMessage } from "../config/mqttClient";
import { fetchServerSettings } from "../config/serverSettings";
import { buildDeviceTooltipHtml, getDeviceTooltipLabels, resolveDeviceTooltipData } from "../config/deviceTooltip";

type MapsControlsProps = {
  localizationService: any;
};

const DEVICE_MOVE_ANIMATION_MS = 100;
const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

type MarkerAnimation = { fromX: number; fromY: number; toX: number; toY: number; start: number };

const MapsControls = ({ localizationService }: MapsControlsProps) => {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  return (
    <div className="maps-controls-bar">
      <button
        type="button"
        className="k-button static-map"
        title={localizationService.toLanguageString('map.zoom_in', 'NOT_FOUND')}
        aria-label={localizationService.toLanguageString('map.zoom_in', 'NOT_FOUND')}
        onClick={() => zoomIn()}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M12 5v14" />
          <path d="M5 12h14" />
        </svg>
      </button>
      <button
        type="button"
        className="k-button static-map"
        title={localizationService.toLanguageString('map.zoom_out', 'NOT_FOUND')}
        aria-label={localizationService.toLanguageString('map.zoom_out', 'NOT_FOUND')}
        onClick={() => zoomOut()}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M5 12h14" />
        </svg>
      </button>
      <button
        type="button"
        className="k-button static-map"
        title={localizationService.toLanguageString('map.reset_zoom', 'NOT_FOUND')}
        aria-label={localizationService.toLanguageString('map.reset_zoom', 'NOT_FOUND')}
        onClick={() => resetTransform()}
      >
        <SvgIcon icon={currentLocation} />
      </button>
    </div>
  );
};


/**
 * Component that displays and manages a map interface with various functionalities.
 * It allows users to interact with maps, zones, groups, beacons, and alarms.
 * Handles data fetching from server and local storage, and renders map layers and markers.
 * Provides options to toggle live updates, and to manage selections using checkboxes and dropdowns.
 * 
 * @param {Object} props - The properties passed to the component.
 */
export const Maps = (props: any) => {

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const localizationService = useLocalization();
  const [instance, accessTokenRequest] = AccessToken();

  const [selectedMap, setSelectedMap] = useState<any>([]);

  const [listMaps, setListMaps] = useState([]);
  const [mapName, setMapName] = useState([]);
  const [listMapNames, setListMapNames] = useState([]);

  const [zonesCheckboxByMap, setZonesCheckboxByMap] = useState<any>([]);
  const [zoneCheckedList, setZoneCheckedList] = useState<any>([]);

  const [groupsCheckboxByMap, setGroupsCheckboxByMap] = useState<any>([]);
  const [groupChanged, setGroupChanged] = useState<any>(false);

  const [deviceChecked, setDeviceChecked] = useState<any>([]);

  const [allBeacons, setAllBeacons] = useState<any>([]);
  const [listBeaconsOn, setListBeaconsOn] = useState<any>([]);

  const [currentZoneCanvas, setCurrentZoneCanvas] = useState<any>([]);

  const [mapGeoreferenced, setMapGeoreferenced] = useState(false);

  const [loading, setLoading] = useState(false);
  const isChangingMapRef = useRef(false);
  const hasRefreshedForSynoptic = useRef(false);

  const navigate = useNavigate()

  let mapCanvas: any = useRef()
  let mapCtx = mapCanvas?.current?.getContext("2d");

  const zoneCanvas: any = useRef();
  const groupCanvas: any = useRef();
  const alarmCanvas: any = useRef();

  // SVG marker state for better quality
  const [groupMarkersSVG, setGroupMarkersSVG] = useState<any>([]);
  const [beaconMarkersSVG, setBeaconMarkersSVG] = useState<any>([]);

  // Marker interpolation: displayed tip position per MAC and the active animations toward the new target.
  const markerDisplayRef = useRef<Record<string, { x: number; y: number }>>({});
  const markerAnimationsRef = useRef<Record<string, MarkerAnimation>>({});
  const markerAnimationFrameRef = useRef<number | null>(null);
  const [markerOffsets, setMarkerOffsets] = useState<Record<string, { dx: number; dy: number }>>({});

  const deviceMacFromSynoptic: any = localStorage.getItem('deviceMacFromSynoptic')
  const deviceMapIdFromSynoptic: any = localStorage.getItem('deviceMapIdFromSynoptic')
  const beaconId: any = localStorage.getItem('beaconId')
  const mapIdByBeacon: any = localStorage.getItem('mapIdByBeacon')
  const safeJsonParse = <T,>(value: string | null, fallback: T): T => {
    if (value == null || value === '') {
      return fallback;
    }

    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  };

  const parseStoredMapId = (value: string | null): number | null => {
    if (value == null || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }

    const parsed = safeJsonParse<number | string>(value, value);
    const normalized = typeof parsed === 'number' ? parsed : Number(parsed);

    return Number.isFinite(normalized) ? normalized : null;
  };

  const alarmInfoSelected: any = safeJsonParse(localStorage.getItem('alarmInfoSelected'), null)

  const [alarmChecked, setAlarmChecked] = useState<any>(alarmInfoSelected);
  const [alarmToggle, setAlarmToggle] = useState<any>(false);
  const [alarmInfo, setAlarmInfo] = useState<any>([]);

  const [liveStatusMap, setLiveStatus] = useState<any>(false);
  const [mapId, setMapId] = useState<any>(null);

  const setStoredMapId = (value: number | string | null) => {
    if (value == null || value === '') {
      localStorage.setItem('mapId', '');
      setMapId('');
      return;
    }

    const serialized = JSON.stringify(value);
    localStorage.setItem('mapId', serialized);
    setMapId(serialized);
  };

  const [showDeviceMacBySynoptic, setShowDeviceMacBySynoptic] = useState<any>(false);
  const [deviceMacSelected, setDeviceMacSelected] = useState<any>(deviceMacFromSynoptic);
  const [noPositionFound, setNoPositionFound] = useState<any>(false);

  const [showBeaconMac, setShowBeaconMac] = useState<any>(false);
  const [beaconIdSelected, setBeaconIdSelected] = useState<any>(beaconId);
  const [beaconsFromGrid, setBeaconsFromGrid] = useState<any>([]);
  const [selectedBeacons, setSelectedBeacons] = useState<any>([]);
  const [beaconFilter, setBeaconFilter] = useState<string>('');

  const [onlyBound, setOnlyBound] = useState<any>(false);
  const [toggleGeoStatic, setToggleGeoStatic] = useState<any>(true);
  const [groupingEnabled, setGroupingEnabled] = useState<boolean>(() => {
    const storedGrouping = localStorage.getItem('groupingEnabledMap');
    if (storedGrouping == null) {
      return false;
    }
    return storedGrouping === 'true';
  });
  const [zonesPanelExpanded, setZonesPanelExpanded] = useState(true);
  const [groupsPanelExpanded, setGroupsPanelExpanded] = useState(true);
  const [beaconsPanelExpanded, setBeaconsPanelExpanded] = useState(true);
  const [noMaps, setNoMaps] = useState<any>(false);

  //manager tooltip groups
  const [transformState, setTransformState] = useState<any>([])
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [tooltipVariant, setTooltipVariant] = useState<'default' | 'device' | 'device-flipped'>('default');
  const [showTooltip, setShowTooltip] = useState(false);
  const [lastMouseEvent, setLastMouseEvent] = useState(null);
  const transformContentRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  let languageId = localStorage.getItem('languageId');

  const [mqttNotifications, setMqttNotifications] = useState<Array<{ id: number; message: string; style: 'info' | 'warning' | 'error' | 'success' }>>([]);
  const [latestMqttDeviceUpdate, setLatestMqttDeviceUpdate] = useState<any>(null);
  const [mqttClientRetryKey, setMqttClientRetryKey] = useState(0);
  const wakeupTopicRef = useRef('');
  const hasFetchedWakeupTopicRef = useRef(false);
  const wakeupTopicRequestRef = useRef<Promise<void> | null>(null);
  const mqttPositionCacheRef = useRef<Record<string, { position: any; updatedAt: number }>>({});

  const normalizeDeviceMac = (value: any): string =>
    typeof value === 'string' ? value.trim().toLowerCase() : '';
  
  const normalizeGroupSelectionId = (value: any): string => {
    if (value === null || value === undefined || value === -1 || value === '-1') {
      return '__NO_GROUP__';
    }

    const normalized = String(value).trim();

    if (normalized === '' || normalized === 'null' || normalized === 'undefined') {
      return '__NO_GROUP__';
    }

    return normalized;
  };

  const getDeviceGroupSelectionId = (device: any): string =>
    normalizeGroupSelectionId(device?.groupId ?? device?.GroupId ?? device?.idGroup ?? device?.group?.id);

  const applyCachedMqttPositions = (devices: any[]) => {
    const list = Array.isArray(devices) ? devices : [];

    return list.map((device: any) => {
      const normalizedMac = normalizeDeviceMac(device?.deviceMac ?? device?.mac ?? device?.DeviceMac);
      if (!normalizedMac) {
        return device;
      }

      const cached = mqttPositionCacheRef.current[normalizedMac];
      if (!cached?.position) {
        return device;
      }

      const mergedPosition = {
        ...(device?.position ?? {}),
        ...(cached.position ?? {})
      };

      const latitude = Number(mergedPosition?.latitude);
      const longitude = Number(mergedPosition?.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return device;
      }

      return {
        ...device,
        position: mergedPosition,
        //Position: {
        //  ...(device?.Position ?? {}),
        //  ...mergedPosition,
        //},
      };
    });
  };
    
  useEffect(() => {
      const client = getSharedMqttClient(SERVER_URL);
      if (!client) {
        const retryTimer = window.setTimeout(() => {
          setMqttClientRetryKey((prev) => prev + 1);
        }, 1500);

        return () => {
          window.clearTimeout(retryTimer);
        };
      }

      let isDisposed = false;

      const mqttTopicMatches = (topic: string, filter: string) => {
        if (!filter) {
          return false;
        }

        const topicLevels = topic.split('/');
        const filterLevels = filter.split('/');

        for (let i = 0; i < filterLevels.length; i++) {
          const filterLevel = filterLevels[i];
          const topicLevel = topicLevels[i];

          if (filterLevel === '#') {
            return true;
          }

          if (topicLevel === undefined) {
            return false;
          }

          if (filterLevel === '+' || filterLevel === '*') {
            continue;
          }

          if (filterLevel !== topicLevel) {
            return false;
          }
        }

        return topicLevels.length === filterLevels.length;
      };

      const applyWakeupTopic = (nextTopicRaw: any) => {
        const nextTopic = typeof nextTopicRaw === 'string' ? nextTopicRaw.trim() : '';
        if (nextTopic === wakeupTopicRef.current) {
          return;
        }

        const previousTopic = wakeupTopicRef.current;
        wakeupTopicRef.current = nextTopic;

        if (previousTopic) {
          client.unsubscribe(previousTopic);
        }

        if (wakeupTopicRef.current && client.connected) {
          client.subscribe(wakeupTopicRef.current);
        }
      };

      const resolveWakeupTopicFromSettings = async () => {
        if (hasFetchedWakeupTopicRef.current && wakeupTopicRef.current) {
          return;
        }

        if (wakeupTopicRequestRef.current) {
          await wakeupTopicRequestRef.current;
          return;
        }

        const applySettings = (settings: any[]) => {
          const wakeupTopicSetting = settings?.find((setting: any) => setting.id == 'MqttWebSocket_WakeupTopic')?.value;
          applyWakeupTopic(wakeupTopicSetting);
        };

        wakeupTopicRequestRef.current = (async () => {
          const fetchSettingsWithToken = async (accessToken: any): Promise<any[] | null> => {
            try {
              const currentLanguageId = localStorage.getItem('languageId');
              return await fetchServerSettings(SERVER_URL, accessToken, currentLanguageId);
            } catch {
              return null;
            }
          };

          const acquireFreshAccessToken = async (): Promise<string | null> => {
            await instance.initialize();

            const account = accessTokenRequest?.account
              ?? instance.getActiveAccount()
              ?? instance.getAllAccounts()?.[0];

            if (!account) {
              return null;
            }

            const activeAccount = instance.getActiveAccount();
            if (!activeAccount || activeAccount.homeAccountId !== account.homeAccountId) {
              instance.setActiveAccount(account);
            }

            const tokenRequest: any = {
              ...accessTokenRequest,
              account,
            };

            const accessTokenResponse: any = await instance.acquireTokenSilent(tokenRequest);
            const nextAccessToken = accessTokenResponse?.accessToken ?? null;

            if (nextAccessToken) {
              localStorage.setItem('accessToken', nextAccessToken);
            }

            return nextAccessToken;
          };

          let settings: any[] | null = null;

          const storedAccessToken = localStorage.getItem('accessToken');
          if (storedAccessToken != null) {
            settings = await fetchSettingsWithToken(storedAccessToken);
          }

          if (!settings) {
            const refreshedAccessToken = await acquireFreshAccessToken();
            if (refreshedAccessToken) {
              settings = await fetchSettingsWithToken(refreshedAccessToken);
            }
          }

          if (settings) {
            applySettings(settings ?? []);
            hasFetchedWakeupTopicRef.current = true;
            return;
          }

          hasFetchedWakeupTopicRef.current = false;
          applyWakeupTopic('');
        })()
          .catch((error: any) => {
            hasFetchedWakeupTopicRef.current = false;
            applyWakeupTopic('');

            if (error instanceof InteractionRequiredAuthError) {
              instance.acquireTokenRedirect(accessTokenRequest);
            }
          })
          .finally(() => {
            wakeupTopicRequestRef.current = null;
          });

        await wakeupTopicRequestRef.current;
      };

      const onWakeupTopicChanged = (event: Event) => {
        const customEvent = event as CustomEvent<{ topic?: string }>;
        applyWakeupTopic(customEvent?.detail?.topic ?? '');
      };

      const onConnect = async () => {
        if (!wakeupTopicRef.current) {
          await resolveWakeupTopicFromSettings();
        }

        if (isDisposed || !wakeupTopicRef.current) {
          return;
        }

        client.subscribe(wakeupTopicRef.current);
      };
  
      const onMessage = (topic: string, message: Buffer) => {
  
        const rawMessage = message.toString();
        let parsedText: any = null;
        const parsedMessage: any = parseMqttMessage(rawMessage);

        if (parsedMessage && typeof parsedMessage === 'object') {
          parsedText = parsedMessage;
        } else if (typeof parsedMessage === 'string' && parsedMessage.trim() !== '') {
          try {
            parsedText = JSON.parse(parsedMessage);
          } catch {
            parsedText = null;
          }
        }
  
        if (wakeupTopicRef.current && mqttTopicMatches(topic, wakeupTopicRef.current) && parsedText != null) {
          setMqttNotifications(prev => [...prev, { id: Date.now(), message: parsedText, style: 'info' }]);
          setLatestMqttDeviceUpdate(parsedText);
        }
        
      };
  
      client.on('connect', onConnect);
      client.on('message', onMessage);
      window.addEventListener('settings:mqtt-wakeup-topic-changed', onWakeupTopicChanged as EventListener);
  
      if (client.connected) {
        onConnect();
      }
  
      return () => {
        isDisposed = true;
        if (wakeupTopicRef.current) {
          client.unsubscribe(wakeupTopicRef.current);
        }
        client.off('connect', onConnect);
        client.off('message', onMessage);
        window.removeEventListener('settings:mqtt-wakeup-topic-changed', onWakeupTopicChanged as EventListener);
      };
    }, [SERVER_URL, instance, accessTokenRequest, mqttClientRetryKey]);

  useLayoutEffect(() => {
    if (markerAnimationFrameRef.current != null) {
      cancelAnimationFrame(markerAnimationFrameRef.current);
      markerAnimationFrameRef.current = null;
    }

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    const now = performance.now();
    const previousDisplay = markerDisplayRef.current;
    const previousAnimations = markerAnimationsRef.current;
    const nextDisplay: Record<string, { x: number; y: number }> = {};
    const nextAnimations: Record<string, MarkerAnimation> = {};

    (Array.isArray(groupMarkersSVG) ? groupMarkersSVG : []).forEach((marker: any) => {
      const key = normalizeDeviceMac(marker?.deviceMac);
      const toX = Number(marker?.tipX);
      const toY = Number(marker?.tipY);
      if (!key || !Number.isFinite(toX) || !Number.isFinite(toY)) {
        return;
      }

      const running = previousAnimations[key];
      // Re-renders with the same target (e.g. zoom) must not restart a running animation.
      if (running && running.toX === toX && running.toY === toY) {
        nextAnimations[key] = running;
        nextDisplay[key] = previousDisplay[key] ?? { x: toX, y: toY };
        return;
      }

      const previous = previousDisplay[key];
      if (!reduceMotion && previous && Math.hypot(previous.x - toX, previous.y - toY) > 0.5) {
        nextAnimations[key] = { fromX: previous.x, fromY: previous.y, toX, toY, start: now };
        nextDisplay[key] = previous;
      } else {
        nextDisplay[key] = { x: toX, y: toY };
      }
    });

    markerDisplayRef.current = nextDisplay;
    markerAnimationsRef.current = nextAnimations;

    const computeOffsets = (time: number) => {
      const offsets: Record<string, { dx: number; dy: number }> = {};
      let active = false;

      Object.entries(markerAnimationsRef.current).forEach(([key, animation]) => {
        const progress = Math.min(1, Math.max(0, (time - animation.start) / DEVICE_MOVE_ANIMATION_MS));
        const eased = easeInOutCubic(progress);
        const x = animation.fromX + (animation.toX - animation.fromX) * eased;
        const y = animation.fromY + (animation.toY - animation.fromY) * eased;
        markerDisplayRef.current[key] = { x, y };

        if (progress < 1) {
          offsets[key] = { dx: x - animation.toX, dy: y - animation.toY };
          active = true;
        } else {
          delete markerAnimationsRef.current[key];
        }
      });

      setMarkerOffsets(offsets);
      return active;
    };

    const step = (time: number) => {
      markerAnimationFrameRef.current = computeOffsets(time) ? requestAnimationFrame(step) : null;
    };

    if (computeOffsets(now)) {
      markerAnimationFrameRef.current = requestAnimationFrame(step);
    }
  }, [groupMarkersSVG]);

  useEffect(() => () => {
    if (markerAnimationFrameRef.current != null) {
      cancelAnimationFrame(markerAnimationFrameRef.current);
    }
  }, []);

  useEffect(() => {
    getMapList()
  }, []);

  useEffect(() => {
    const syncMapId = () => {
      setMapId(localStorage.getItem('mapId'));
    };

    syncMapId();
    window.addEventListener('storage', syncMapId);

    return () => {
      window.removeEventListener('storage', syncMapId);
    };
  }, []);

  useEffect(() => {
    if (zoneCanvas.current != null) {
      setCurrentZoneCanvas(zoneCanvas);
    }
  }, [mapGeoreferenced]);

  // reset localstorage on page load 
  useEffect(() => {
    localStorage.setItem('zonesChecked', '');
    localStorage.setItem('devicesChecked', '');
    localStorage.removeItem('onlyBoundMap');
    setShowDeviceMacBySynoptic(false);
    setShowBeaconMac(false);
    setSelectedBeacons([]);
  }, [])

  // Ensure synoptic deep link refreshes positions immediately on load
  useEffect(() => {
    if (!deviceMacFromSynoptic) {
      return;
    }

    if (hasRefreshedForSynoptic.current) {
      return;
    }

    const storedMapId = mapId ?? localStorage.getItem('mapId');
    const parsedMapId = parseStoredMapId(storedMapId as string | null);
    if (parsedMapId == null) {
      return;
    }

    refreshGroupPositionsInMap(parsedMapId, mapGeoreferenced);
    hasRefreshedForSynoptic.current = true;
  }, [deviceMacFromSynoptic, mapGeoreferenced, mapId]);

  const getMapList = async () => {

    setLoading(true)

    setStoredMapId(null);

    const commonOperations = async (accessToken: any) => {
      try {
        const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
        const res = await axios.get(`${SERVER_URL}/map/full/list`, { headers });
        const data = res?.data.data;

        if (!data?.length) {
          setNoMaps(true);
          setLoading(false);
          return;
        }

        const mapsList = data;
        const mapNames = data.map((map: any) => map.name);
        const mainMap = data.find((map: any) => map.main == '1');

        setListMaps(mapsList);
        setListMapNames(mapNames);

        getGroupList();

        if (deviceMacFromSynoptic != null) {
          if (!deviceMapIdFromSynoptic) {
            setNoPositionFound(true);
            setTimeout(() => setNoPositionFound(false), 5000);
            return;
          }

          const mapSelected = mapsList.find((map: any) => map.id === Number(deviceMapIdFromSynoptic)) ?? mapsList[0];
          setSelectedMap(mapSelected);
          setStoredMapId(mapSelected.id);
          localStorage.removeItem('mapGeoreferenced');
          await changeMap(mapSelected, false, mapsList);
          setShowDeviceMacBySynoptic(true);
          return;
        }

        else if (mapIdByBeacon != null) {
          const mapSelected = mapsList.find((map: any) => map.id === Number(mapIdByBeacon)) ?? mapsList[0];
          if (mapSelected) {
            localStorage.removeItem('mapGeoreferenced');
            await changeMap(mapSelected, false, mapsList);
            setSelectedMap(mapSelected);
            setStoredMapId(mapSelected.id);
            setShowBeaconMac(true);
            return;
          }
        }

        else if (alarmInfoSelected != null) {
          localStorage.removeItem('mapGeoreferenced');
          const nextAlarmChecked = {
            ...alarmInfoSelected,
            checked: true
          };

          localStorage.setItem("alarmInfoSelected", JSON.stringify(nextAlarmChecked));
          //setAlarmToggle(true)
          setAlarmChecked(nextAlarmChecked);
          setAlarmInfo(alarmInfoSelected);
          getAlarmPositionByWakeupId(alarmInfoSelected?.info?.id, mapsList);
          return;
        }
        else {
          const currMapSelected = (() => {
            try {
              const parsed = selectedMap || 'null';
              return parsed;
            } catch {
              return null;
            }
          })();

          const fallbackMap =
            (Array.isArray(currMapSelected) && currMapSelected.length > 0 && currMapSelected) ||
            (!Array.isArray(currMapSelected) && typeof currMapSelected === 'object' && currMapSelected) ||
            mainMap ||
            mapsList[0];

          const fallbackMapId = fallbackMap?.id ?? '-1';

          setStoredMapId(fallbackMapId);
          setSelectedMap(fallbackMap);
          await changeMap(fallbackMap, false, mapsList);
        }

      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(accessTokenRequest);
        }
      }
      //finally {
      //  setLoading(false);
      //}
    };


    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }
  }

  // refresh groups positions in map
  useEffect(() => {
    const refreshApiRaw = Number(localStorage.getItem('refreshAPI'));
    const refreshApiSeconds = Number.isFinite(refreshApiRaw) && refreshApiRaw > 0 ? refreshApiRaw : 15;

    const interval = setInterval(async () => {
      // When MQTT live updates are enabled, skip polling-based refresh.
      if (isMqttConnectionEnabled()) {
        return;
      }

      // Skip refresh if map is being changed
      if (isChangingMapRef.current) return;

      const currentMapId = parseStoredMapId(localStorage.getItem('mapId'));
      if (currentMapId == null || currentMapId === -1) {
        return;
      }

      const isMapGeoreferenced = localStorage.getItem('mapGeoreferenced') === 'true';

      refreshGroupPositionsInMap(currentMapId, isMapGeoreferenced)
    }, refreshApiSeconds * 1000);

    return () => clearInterval(interval);
  }, [transformState])

  const stringToBoolean = (str: any) => {
    return str?.toLowerCase() === 'true';
  }

  const markerColorStyle = (color: string | undefined) => ({
    ['--marker-color' as any]: color ?? 'transparent'
  });

  const [mapImageWidth, setMapImageWidth] = useState<any>(0);
  const [mapImageHeight, setMapImageHeight] = useState<any>(0);
  const [mapImageData, setMapImageData] = useState<string | null>(null);

  const getLatLong = (position: any) => {
    const longitude = Number(position?.longitude);
    const latitude = Number(position?.latitude);

    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      return null;
    }

    return { longitude, latitude };
  };

  const projectPointOnCanvas = (position: any, width: number, height: number) => {
    if (width <= 0 || height <= 0) {
      return null;
    }

    const coords = getLatLong(position);
    if (!coords) {
      return null;
    }

    const resolveAxisValue = (value: number, limit: number) => {
      if (!Number.isFinite(value)) {
        return null;
      }

      if (Math.abs(value) <= 1) {
        return limit * value;
      }

      return value;
    };

    const x = resolveAxisValue(coords.longitude, width);
    const y = resolveAxisValue(coords.latitude, height);

    if (x == null || y == null) {
      return null;
    }

    return {
      x,
      y: height - y
    };
  };

  //mqtt
  useEffect(() => {
    if (!latestMqttDeviceUpdate) {
      return;
    }

    const incomingDevice: any = latestMqttDeviceUpdate;

    const normalizeMac = (value: any): string => {
      return normalizeDeviceMac(value);
    };

    const normalizeGroupId = (value: any): string => {
      if (value === null) {
        return 'null';
      }

      if (value === undefined) {
        return '';
      }

      return String(value).trim();
    };

    const storedDevicesList = safeJsonParse<any[]>(localStorage.getItem('devicesList'), []);
    const groupsSelectionModel = safeJsonParse<any[]>(localStorage.getItem('groupsCheckboxByMap'), []);
    const hasGroupsSelectionModel = Array.isArray(groupsSelectionModel) && groupsSelectionModel.length > 0;
    const checkedGroupIds = new Set(groupsSelectionModel
      .filter((group: any) => group?.checked != false && group?.id !== '0')
      .map((group: any) => normalizeGroupSelectionId(group?.id))
    );

    const resolveDeviceGroupId = (device: any, fallbackList: any[] = []): string => {
      const directGroupId = normalizeGroupSelectionId(
        device?.groupId ?? device?.GroupId ?? device?.idGroup ?? device?.group?.id
      );

      if (directGroupId !== '__NO_GROUP__') {
        return directGroupId;
      }

      const macKey = normalizeMac(device?.deviceMac ?? device?.mac ?? device?.DeviceMac);
      if (!macKey) {
        return '__NO_GROUP__';
      }

      const lookupList = [...fallbackList, ...storedDevicesList];
      const matchedDevice = lookupList.find((entry: any) => {
        const entryMac = normalizeMac(entry?.deviceMac ?? entry?.mac ?? entry?.DeviceMac);
        return entryMac === macKey;
      });

      return normalizeGroupSelectionId(
        matchedDevice?.groupId ?? matchedDevice?.GroupId ?? matchedDevice?.idGroup ?? matchedDevice?.group?.id
      );
    };

    const nextDeviceMac = normalizeMac(incomingDevice?.DeviceMac ?? incomingDevice?.deviceMac ?? incomingDevice?.mac);

    const incomingPosition = incomingDevice?.position ?? incomingDevice?.Position;

    const normalizePosition = (rawPosition: any = {}, fallbackPosition: any = {}) => {
      const latitude = Number(
        rawPosition?.latitude ?? rawPosition?.Latitude ?? rawPosition?.lat?? rawPosition?.Y ?? fallbackPosition?.latitude
      );
      const longitude = Number(
        rawPosition?.longitude ?? rawPosition?.Longitude ?? rawPosition?.lon ?? rawPosition?.X ?? fallbackPosition?.longitude
      );
      const elevation = Number(
        rawPosition?.elevation ?? rawPosition?.Elevation ?? rawPosition?.Z ?? fallbackPosition?.elevation
      );
      const orientation = Number(
        rawPosition?.orientation ?? rawPosition?.Orientation ?? fallbackPosition?.orientation
      );

      return {
        latitude: Number.isFinite(latitude) ? latitude : fallbackPosition?.latitude,
        longitude: Number.isFinite(longitude) ? longitude : fallbackPosition?.longitude,
        elevation: Number.isFinite(elevation) ? elevation : fallbackPosition?.elevation,
        orientation: Number.isFinite(orientation) ? orientation : fallbackPosition?.orientation,
      };
    };

    const buildIncomingNormalizedPosition = (fallbackPosition: any = {}) => {
      return normalizePosition(incomingPosition, fallbackPosition);
    };

    const selectedMapCorners = [
      selectedMap?.botLeft,
      selectedMap?.botRight,
      selectedMap?.topLeft,
      selectedMap?.topRight,
    ]
      .map((corner: any) => ({
        longitude: Number(corner?.longitude),
        latitude: Number(corner?.latitude),
      }))
      .filter((corner: any) => Number.isFinite(corner.longitude) && Number.isFinite(corner.latitude));

    const selectedMapBounds = (() => {
      if (selectedMapCorners.length > 0) {
        return {
          minLongitude: Math.min(...selectedMapCorners.map((corner: any) => corner.longitude)),
          maxLongitude: Math.max(...selectedMapCorners.map((corner: any) => corner.longitude)),
          minLatitude: Math.min(...selectedMapCorners.map((corner: any) => corner.latitude)),
          maxLatitude: Math.max(...selectedMapCorners.map((corner: any) => corner.latitude)),
        };
      }

      if (!mapGeoreferenced) {
        return {
          minLongitude: 0,
          maxLongitude: 1,
          minLatitude: 0,
          maxLatitude: 1,
        };
      }

      return null;
    })();

    const isPositionWithinSelectedMap = (position: any) => {
      const latitude = Number(position?.latitude);
      const longitude = Number(position?.longitude);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return false;
      }

      if (!selectedMapBounds) {
        return true;
      }

      return (
        longitude >= selectedMapBounds.minLongitude
        && longitude <= selectedMapBounds.maxLongitude
        && latitude >= selectedMapBounds.minLatitude
        && latitude <= selectedMapBounds.maxLatitude
      );
    };

    const normalizedIncomingPosition = buildIncomingNormalizedPosition({});
    const incomingWithinSelectedMap = isPositionWithinSelectedMap(normalizedIncomingPosition);

    const upsertDeviceListByCoordinates = (devices: any[], options?: { respectSelectedGroups?: boolean }) => {
      const list = Array.isArray(devices) ? devices : [];
      const respectSelectedGroups = options?.respectSelectedGroups === true;
      let hasUpdated = false;

      const areDevicesEquivalent = (left: any, right: any) => {
        try {
          return JSON.stringify(left) === JSON.stringify(right);
        } catch {
          return false;
        }
      };

      if (!nextDeviceMac || !incomingWithinSelectedMap) {
        return { updatedDevices: list, hasUpdated: false };
      }

      let matchIndex = -1;
      let minDistance = Number.POSITIVE_INFINITY;

      list.forEach((device: any, index: number) => {
        const currentMac = normalizeMac(device?.deviceMac ?? device?.mac ?? device?.DeviceMac);
        if (currentMac !== nextDeviceMac) {
          return;
        }

        const devicePosition = normalizePosition(device?.position ?? device?.Position, {});

        if (!isPositionWithinSelectedMap(devicePosition)) {
          return;
        }

        const distance = Math.hypot(
          Number(devicePosition?.latitude) - Number(normalizedIncomingPosition?.latitude),
          Number(devicePosition?.longitude) - Number(normalizedIncomingPosition?.longitude)
        );

        if (!Number.isFinite(distance) || distance >= minDistance) {
          return;
        }

        minDistance = distance;
        matchIndex = index;
      });

      let updatedDevices = list.map((device: any, index: number) => {
        if (index !== matchIndex) {
          return device;
        }

        const resolvedMac = incomingDevice?.mac ?? incomingDevice?.deviceMac ?? incomingDevice?.DeviceMac ?? device?.mac ?? device?.deviceMac;

        const mergedDevice = {
          ...device,
          ...incomingDevice,
          //mac: resolvedMac,
          deviceMac: incomingDevice?.deviceMac ?? incomingDevice?.mac ?? device?.deviceMac ?? device?.mac,
          position: normalizedIncomingPosition,
          //Position: {
          //  ...(device?.Position ?? {}),
          //  ...(incomingDevice?.Position ?? {}),
          //  ...normalizedIncomingPosition,
          //},
        };

        if (areDevicesEquivalent(device, mergedDevice)) {
          return device;
        }

        hasUpdated = true;
        return mergedDevice;
      });

      if (matchIndex === -1) {
        if (nextDeviceMac) {
          const withoutStaleMac = updatedDevices.filter((device: any) => {
            const currentMac = normalizeMac(device?.deviceMac ?? device?.mac ?? device?.DeviceMac);
            return currentMac !== nextDeviceMac;
          });

          if (withoutStaleMac.length !== updatedDevices.length) {
            updatedDevices = withoutStaleMac;
            hasUpdated = true;
          }
        }

        if (respectSelectedGroups && hasGroupsSelectionModel) {
          const incomingGroupId = resolveDeviceGroupId(incomingDevice, updatedDevices);
          if (!checkedGroupIds.has(incomingGroupId)) {
            return { updatedDevices, hasUpdated };
          }
        }

        const resolvedMac = incomingDevice?.mac ?? incomingDevice?.deviceMac ?? incomingDevice?.DeviceMac;

        updatedDevices.push({
          ...incomingDevice,
          //mac: resolvedMac,
          deviceMac: incomingDevice?.deviceMac ?? resolvedMac,
          //checked: incomingDevice?.checked ?? true,
          position: normalizedIncomingPosition,
          //Position: {
          //  ...(incomingDevice?.Position ?? {}),
          //  ...normalizedIncomingPosition,
          //},
        });
        hasUpdated = true;
      }

      if (respectSelectedGroups && hasGroupsSelectionModel) {
        const filteredDevices = updatedDevices.filter((device: any) => checkedGroupIds.has(resolveDeviceGroupId(device, updatedDevices)));

        if (filteredDevices.length !== updatedDevices.length) {
          updatedDevices = filteredDevices;
          hasUpdated = true;
        }
      }

      return { updatedDevices, hasUpdated };
    };

    const hasIncomingCoordinates = Number.isFinite(Number(normalizedIncomingPosition?.latitude))
      && Number.isFinite(Number(normalizedIncomingPosition?.longitude));
    if (hasIncomingCoordinates && nextDeviceMac) {
      mqttPositionCacheRef.current[nextDeviceMac] = {
        position: normalizedIncomingPosition,
        updatedAt: Date.now(),
      };
    }

    const devicesCheckedSource = Array.isArray(deviceChecked) && deviceChecked.length > 0
      ? deviceChecked
      : safeJsonParse<any[]>(localStorage.getItem('devicesChecked'), []);

    const { updatedDevices, hasUpdated } = upsertDeviceListByCoordinates(devicesCheckedSource, { respectSelectedGroups: true });

    if (!hasUpdated) {
      return;
    }

    localStorage.setItem('devicesChecked', JSON.stringify(updatedDevices));
    setDeviceChecked(updatedDevices);

    const updatedDevicesList = upsertDeviceListByCoordinates(storedDevicesList);
    if (updatedDevicesList.hasUpdated) {
      localStorage.setItem('devicesList', JSON.stringify(updatedDevicesList.updatedDevices));
    }

    let devicesForMarkerRefresh = updatedDevices;
    const isMapGeoreferenced = localStorage.getItem('mapGeoreferenced') === 'true';
    if (isMapGeoreferenced) {
      if (localStorage.getItem('liveStatusMap') === 'true') {
        const storedDevicesLive = safeJsonParse<any[]>(localStorage.getItem('devicesLive'), []);
        const updatedDevicesLive = upsertDeviceListByCoordinates(storedDevicesLive, { respectSelectedGroups: true });
        if (updatedDevicesLive.hasUpdated) {
          localStorage.setItem('devicesLive', JSON.stringify(updatedDevicesLive.updatedDevices));
          setDeviceChecked(updatedDevicesLive.updatedDevices);
          devicesForMarkerRefresh = updatedDevicesLive.updatedDevices;
        }
      }else{
        setDeviceChecked(updatedDevices);
        devicesForMarkerRefresh = updatedDevices;
      }
    }

    if (mapImageWidth <= 0 || mapImageHeight <= 0) {
      return;
    }

    const devicesCheckedByMapStored: any = safeJsonParse<any[]>(localStorage.getItem('devicesChecked'), []);
    const deviceCheckedIds = devicesCheckedByMapStored
      .filter((item: any) => item.checked != false)
      .map((item: any) => normalizeMac(item?.deviceMac ?? item?.mac ?? item?.DeviceMac))
      .filter((mac: string) => mac !== '');

    const scale = transformState?.scale ?? 1;
    const newSVGMarkers: any[] = [];

    devicesForMarkerRefresh.forEach((device: any) => {
      const deviceMacKey = normalizeMac(device?.deviceMac ?? device?.mac ?? device?.DeviceMac);
      if (deviceCheckedIds.length > 0 && !deviceCheckedIds.includes(deviceMacKey)) {
        return;
      }

      const projectedPoint = projectPointOnCanvas(device?.position, mapImageWidth, mapImageHeight);
      if (!projectedPoint) {
        return;
      }

      const x = Math.round(projectedPoint.x) + 4.5;
      const y = Math.round(projectedPoint.y) - 4;
      const baseMarkerRadius = 16;
      const baseHoleRadius = 12;
      const offset = 4;
      const angleOffset = Math.PI / 6;

      const leftX = x - offset - Math.sin(angleOffset) * baseMarkerRadius;
      const leftY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;
      const rightX = x - offset + Math.sin(angleOffset) * baseMarkerRadius;
      const rightY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;

      newSVGMarkers.push({
        x: x - offset,
        y: y - offset - baseMarkerRadius,
        markerRadius: baseMarkerRadius,
        holeRadius: baseHoleRadius,
        leftX,
        leftY,
        rightX,
        rightY,
        tipX: x - offset,
        tipY: y,
        color: device?.color ?? device?.groupColor ?? 'lightgrey',
        deviceMac: device?.mac ?? device?.deviceMac,
        deviceName: device?.name,
        scale: 1 / scale,
      });
    });

    setGroupMarkersSVG(newSVGMarkers);
  }, [latestMqttDeviceUpdate, deviceChecked, mapImageHeight, mapImageWidth, mapGeoreferenced, transformState]);

  // Ridisegna i marker SVG quando cambia lo zoom o le dimensioni della mappa
  useEffect(() => {
    if (!mapGeoreferenced && transformState && mapImageWidth > 0 && mapImageHeight > 0) {
      const devicesChecked = safeJsonParse<any[]>(localStorage.getItem('devicesChecked'), []);

      if (devicesChecked.length > 0) {
        const scale = transformState?.scale ?? 1;
        const newSVGMarkers: any = [];

        // Ridisegna i marker solo per i dispositivi giÃ  filtrati
        devicesChecked.forEach((device: any) => {
          const projectedPoint = projectPointOnCanvas(device?.position, mapImageWidth, mapImageHeight);
          if (!projectedPoint) {
            return;
          }

          const x = Math.round(projectedPoint.x) + 4.5;
          const y = Math.round(projectedPoint.y) - 4;

          // Dimensioni fisse del marker
          const baseMarkerRadius = 16;
          const baseHoleRadius = 12;
          const offset = 4;

          const angleOffset = Math.PI / 6;
          const leftX = x - offset - Math.sin(angleOffset) * baseMarkerRadius;
          const leftY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;
          const rightX = x - offset + Math.sin(angleOffset) * baseMarkerRadius;
          const rightY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;

          newSVGMarkers.push({
            x: x - offset,
            y: y - offset - baseMarkerRadius,
            markerRadius: baseMarkerRadius,
            holeRadius: baseHoleRadius,
            leftX,
            leftY,
            rightX,
            rightY,
            tipX: x - offset,
            tipY: y,
            color: device?.color ?? device?.groupColor ?? 'lightgrey',
            deviceMac: device?.mac ?? device?.deviceMac,
            deviceName: device?.name,
            scale: 1 / scale
          });
        });

        setGroupMarkersSVG(newSVGMarkers);
      } else {
        // Se devicesChecked Ã¨ vuoto, svuota i marker
        setGroupMarkersSVG([]);
      }

      // Ridisegna i beacon marker quando cambia lo zoom
      const beaconsList = safeJsonParse<any[]>(localStorage.getItem('beaconsByMapId'), []);

      if (beaconsList.length > 0) {
        const scale = transformState?.scale ?? 1;
        const newBeaconMarkers: any = [];

        beaconsList.forEach((beacon: any) => {
          if (!beacon.checked) {
            return;
          }

          const projectedPoint = projectPointOnCanvas(beacon, mapImageWidth, mapImageHeight);
          if (!projectedPoint) {
            return;
          }

          const x = projectedPoint.x;
          const y = projectedPoint.y;

          newBeaconMarkers.push({
            x: x,
            y: y,
            color: beacon.color,
            name: beacon.name,
            type: beacon.type,
            id: beacon.id,
            scale: 1 / scale
          });
        });

        setBeaconMarkersSVG(newBeaconMarkers);
      } else {
        setBeaconMarkersSVG([]);
      }
    }
  }, [transformState, mapImageWidth, mapImageHeight, mapGeoreferenced, deviceChecked]);

  const renderMap = (map: any) => {

    const isMapGeo: any = stringToBoolean(localStorage.getItem('mapGeoreferenced'))

    if ((map?.georeferenced == 0 || !isMapGeo)) {

      mapCtx?.clearRect(0, 0, mapCtx?.canvas.width, mapCtx?.canvas.height)

      let backgroundCanvas = new Image();

      setMapImageData(map?.image ?? null);
      backgroundCanvas.src = `data:image/jpeg;base64, ${map?.image ?? ''}`;

      backgroundCanvas.onload = () => {
        const wImg = backgroundCanvas.width;
        const hImg = backgroundCanvas.height;

        setMapImageWidth(wImg);
        setMapImageHeight(hImg);

      };

      setMapGeoreferenced(false)
    }
    else {
      setMapImageData(null);
      setMapGeoreferenced(true)
    }
  }

  useEffect(() => {
    if (!mapCanvas.current || mapImageWidth === 0 || !mapImageData) return;

    // imposta dimensioni canvases (groupCanvas e beaconCanvas ora usano SVG)
    mapCanvas.current.width =
      zoneCanvas.current.width =
      alarmCanvas.current.width = mapImageWidth;

    mapCanvas.current.height =
      zoneCanvas.current.height =
      alarmCanvas.current.height = mapImageHeight;

    // disegno la mappa
    const ctx = mapCanvas.current.getContext("2d");
    const img = new Image();
    img.src = "data:image/jpeg;base64," + mapImageData;

    img.onload = () => {
      ctx.clearRect(0, 0, mapImageWidth, mapImageHeight);
      ctx.drawImage(img, 0, 0, mapImageWidth, mapImageHeight);
    };

  }, [mapImageWidth, mapImageHeight, mapGeoreferenced, mapImageData]);


  const [initialScale, setInitialScale] = useState(0.7);

  const changeMap = async (event: any, fromMap?: boolean, mapsList?: any) => {

    isChangingMapRef.current = true;
    setLoading(true);
    setGroupMarkersSVG([]);
    setNoPositionFound(false);

    // Reset scale when changing map
    setInitialScale(0.7)

    let currMap: any = ''
    if (event?.value != undefined) {
      currMap = event?.value ?? event ?? ''
    } else {
      currMap = event.name ?? event ?? ''
    }

    setZoneCheckedList([])
    setZonesCheckboxByMap([])
    setGroupsCheckboxByMap([])
    setListBeaconsOn([])
    setSelectedBeacons([])

    localStorage.setItem('liveStatusMap', 'false');
    setLiveStatus(false);
    localStorage.setItem('onlyBoundMap', 'false');
    setOnlyBound(false);

    localStorage.removeItem('zonesChecked');

    setAlarmInfo([])

    if (fromMap) {
      setAlarmChecked(null)
    }

    const currListMaps: any = listMaps.length > 0 ? listMaps : mapsList

    const mapSelected = currListMaps?.find((map: any) => map.name == currMap)

    const updateMapSelected = mapSelected ?? currListMaps?.find((map: any) => map.id == currMap)

    if (updateMapSelected == null) return

    if ((deviceMacFromSynoptic && deviceMapIdFromSynoptic == updateMapSelected.id)) {
      setShowDeviceMacBySynoptic(true)
    } else {
      setShowDeviceMacBySynoptic(false)
    }
    if (beaconId && mapIdByBeacon == updateMapSelected.id) {
      setShowBeaconMac(true)
      if (beaconsFromGrid.length > 0) changeCheckboxBeacon({ target: { element: { checked: true } } }, beaconId, beaconsFromGrid);
    } else {
      setShowBeaconMac(false)
      changeCheckboxBeacon({ target: { element: { checked: false } } }, beaconId);
      // Rimuovi beaconId e mapIdByBeacon dal localStorage quando si cambia mappa
      if (mapIdByBeacon != null && mapIdByBeacon != updateMapSelected.id) {
        localStorage.removeItem('beaconId');
        localStorage.removeItem('mapIdByBeacon');
        setBeaconIdSelected(null);

      }
    }

    renderMap(updateMapSelected);
    await getMapById(updateMapSelected?.id) // inizialize the mapCanvas

    const isMapGeoStatic: any = localStorage.getItem('mapGeoreferenced') != null
      ? safeJsonParse<boolean | null>(localStorage.getItem('mapGeoreferenced'), null)
      : null
    const isGeoreferenced: any = isMapGeoStatic ?? updateMapSelected?.georeferenced == 1 ? true : false

    localStorage.setItem('mapGeoreferenced', JSON.stringify(isGeoreferenced))
    setToggleGeoStatic(isGeoreferenced);
    setMapGeoreferenced(isGeoreferenced)

    getZoneList(updateMapSelected.id, isGeoreferenced)

    getBeaconList(updateMapSelected.id, true, beaconsFromGrid.length > 0, isGeoreferenced, currListMaps, true)

    setSelectedMap(updateMapSelected)
    setMapName(updateMapSelected.name)
    setStoredMapId(updateMapSelected.id)

    const mapCtx = mapCanvas?.current?.getContext("2d");
    const mapWidth = mapCanvas?.width ?? mapCanvas?.current?.width
    const mapHeight = mapCanvas?.height ?? mapCanvas?.current?.height
    mapCtx?.clearRect(0, 0, mapWidth, mapHeight)

    const zoneCtx = zoneCanvas?.current?.getContext("2d");
    const zoneWidth = zoneCanvas?.width ?? zoneCanvas?.current?.width
    const zoneHeight = zoneCanvas?.height ?? zoneCanvas?.current?.height
    zoneCtx?.clearRect(0, 0, zoneWidth, zoneHeight)

    // reset beacons from map
    changeCheckboxBeacon(false, 0)

    const alarmCtx = alarmCanvas?.current?.getContext("2d");
    const alarmWidth = alarmCanvas?.width ?? alarmCanvas?.current?.width
    const alarmHeight = alarmCanvas?.height ?? alarmCanvas?.current?.height
    alarmCtx?.clearRect(0, 0, alarmWidth, alarmHeight)

    wakeUpGroupByMapId(updateMapSelected.id, isGeoreferenced)
  }

  const getMapById = async (mapId: number) => {

    setLoading(true);

    const commonOperations = (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      axios.get(`${SERVER_URL}/map/${mapId}`, { headers })
        .then((res) => {

          if (res.data == '' || res.data.length == 0) {
            setLoading(false);
            return;
          }

          const data = res.data.data

          let infoMapSelected: any = []
          let botLeft: any = []
          let topLeft: any = []
          let botRight: any = []
          let topRight: any = []

          infoMapSelected.push(data)
          botLeft.push(data.botLeft)
          topLeft.push(data.topLeft)
          botRight.push(data.botRight)
          topRight.push(data.topRight)

          setMapName(data.name)

        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
          setLoading(false);
        });
    }
    setLoading(false);
  }

  const getZoneList = async (mapId: number, isGeoreferenced?: boolean) => {

    if (mapId == null || mapId == undefined) return

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      axios.get(`${SERVER_URL}/zone/bymapid/${mapId}?isGeoreferenced=${isGeoreferenced}`, { headers })
        .then((res) => {
          const data = res?.data?.data

          let listZones: any = []
          let listZonesSelected: any = []
          let listZoneNames: any = []

          // add a checkbox All Zones
          listZones.push({
            id: '0',
            name: localizationService.toLanguageString('map.selectAll', 'NOT_FOUND'),
            color: 'white'
          })

          data?.forEach((zone: any, id: any) => {
            listZones.push(zone)
            listZoneNames.push(zone.name)
            listZonesSelected.push({
              id: zone.id,
              name: zone.name,
              color: zone.color,
              points: zone.points
            })
          });

          setZonesCheckboxByMap(listZones)
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
          setLoading(false);
          navigate("/")
        });
    }
    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }
  }

  const getGroupList = async () => {

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      axios.get(`${SERVER_URL}/job/list?enabled=true`, { headers })
        .then((res) => {
          const jobs = Array.isArray(res?.data?.data) ? res.data.data : [];
          localStorage.setItem('jobsListMap', JSON.stringify(
            jobs
              .filter((job: any) => job?.id != null && job.id !== -1)
              .map((job: any) => ({ id: job.id, name: job.name, color: job.color }))
          ));
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });

      axios.get(`${SERVER_URL}/group/list`, { headers })
        .then((res) => {

          const data = res.data.data.filter((item: any) => item.enabled !== false);

          localStorage.setItem('groupsList', JSON.stringify(data));

          //select all groups by default 
          data?.forEach((group: any) => {
            groupsCheckboxByMap.push(group)
          })

          let devicesToShowInMap = localStorage.getItem('devicesList')
          devicesToShowInMap = groupsCheckboxByMap ?? devicesToShowInMap

          setDeviceChecked(devicesToShowInMap)
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }
    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
        });
    }
  }

  const getBeaconList = async (mapId: number, flag: boolean, fromBeaconGrid?: boolean, isMapGeoreferenced?: any, mapsList?: any, toggleGeoStatic?: any) => {

    setLoading(true);

    if (mapId == null || mapId == undefined) return

    const convertGeoMap = isMapGeoreferenced == '1' ? true : false

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      const params = {
        enabled: flag,
        isGeoreferenced: convertGeoMap
      };
      await axios.get(`${SERVER_URL}/beacon/${mapId}`, { headers, params })
        .then((res) => {
          const data = res?.data?.data

          let beaconsByMapId: any = []

          // add a checkbox All Beacons
          beaconsByMapId.push({
            id: '0',
            name: localizationService.toLanguageString('map.selectAll', 'NOT_FOUND'),
            color: 'white',
            checked: false
          })

          data?.forEach((beacon: any) => {
            if (localStorage.getItem('beaconId') == beacon.id) {
              beaconsByMapId.push(beacon)
              beacon.checked = false
              return
            } else {
              if (beacon.mapId != mapId) {
                return;
              } else {
                beaconsByMapId.push(beacon)
              }
              beacon.checked = false
            }
          });

          if (flag) {
            // format date in data.data
            beaconsByMapId.forEach((beacon: any) => {
              if (beacon?.lastSeen == null) {
                beacon.lastSeen = localizationService.toLanguageString('beacons.date_not_found', 'NOT_FOUND')
                return
              }
              beacon.lastSeen = beacon?.lastSeen?.split('T')[0]

              //format alarm.dateTime with gg/mm/yyyy
              const date = new Date(beacon?.lastSeen);
              const day = date?.getDate();

              const month = (date.getMonth() + 1).toString().padStart(2, "0");
              const year = date.getFullYear();
              beacon.lastSeen = new Date(`${year},${month},${day}`)
            })
            setListBeaconsOn(beaconsByMapId);
            setAllBeacons(beaconsByMapId);
          }

          if (fromBeaconGrid) {
            beaconsByMapId.forEach((beacon: any) => {
              if (beacon.id == beaconId) {
                beacon.checked = true
                getMapByPosition(beacon.longitude, beacon.latitude, beacon.elevation, null, mapsList)
                beaconsByMapId.shift()
                const beaconFromGrid = beaconsByMapId.filter((b: any) => beacon.id.includes(b.id))
                setSelectedBeacons(beaconFromGrid)
                if (beaconFromGrid.length > 0) localStorage.setItem('beaconFromGrid', JSON.stringify(beaconFromGrid))
              }
            });
            setBeaconsFromGrid(beaconsByMapId)
            changeCheckboxBeacon(true, beaconId, beaconsByMapId)
          } else {
            changeCheckboxBeacon(true, beaconId, beaconsByMapId)
            setSelectedBeacons([])
          }

          if (!toggleGeoStatic && !fromBeaconGrid) {
            const updatedListBeaconCheckbox = beaconsByMapId.map((beacon: any) => {
              return {
                ...beacon, // Create a new object based on the existing beacon
                checked: false, // Override the checked property
              };
            })
            setListBeaconsOn(updatedListBeaconCheckbox)
            setAllBeacons(updatedListBeaconCheckbox)
          }

        }).catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
          setLoading(false);
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
          setLoading(false);
        });
    }
  }

  const getMapByPosition = async (positionX: any, positionY: any, elevation: any, orientation?: any, mapsList?: any) => {

    setLoading(true)

    const params: any = {
      longitude: positionX,
      latitude: positionY,
      elevation: elevation,
      ...(orientation != null && { orientation })
    }

    const commonOperations = (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      axios.post(`${SERVER_URL}/map/position`, params, { headers })
        .then(async (res) => {
          const data = res.data.data

          if (data?.length == 0) return

          const mapInfo = data
          await changeMap(mapInfo, false, mapsList)
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
          setLoading(false)
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
          setLoading(false)
        });
    }
  }

  const processMarkersFromData = (data: any, mapId: any, isGeoreferenced?: any) => {
    const mergedData = applyCachedMqttPositions(Array.isArray(data) ? data : []);
    let devicesList: any = mergedData;

    const isLive = localStorage.getItem('liveStatusMap');
    const onlyBound = localStorage.getItem('onlyBoundMap');

    const currDeviceMacFromSynoptic = localStorage.getItem('deviceMacFromSynoptic');

    if (currDeviceMacFromSynoptic != null && deviceMapIdFromSynoptic == mapId) {
      devicesList = devicesList.filter((device: any) =>
        (device?.deviceMac ?? device?.mac) == currDeviceMacFromSynoptic
      );
    }

    if (isLive == 'true') {
      const minutesBefore: any = localStorage.getItem('statusDevice') ?? 75;
      let liveDevicesToShowInMap: any = [];
      let dateBeforeMinutes: any = '';

      devicesList?.map((device: any) => {
        if (device.dateTime == null || device.dateTime == 0) {
          return;
        } else {
          dateBeforeMinutes = getDateRange(minutesBefore);
          device.dateTime = convertDateTime(device?.dateTime);
          const localTimeString = device.dateTime.toLocaleString(languageId, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });
          if (compareDateTimes(localTimeString, dateBeforeMinutes) == 1) {
            liveDevicesToShowInMap.push(device);
          }
        }
      });

      setDeviceChecked(liveDevicesToShowInMap);
      localStorage.setItem('devicesLive', JSON.stringify(liveDevicesToShowInMap));

      const devicesLive = safeJsonParse<any[]>(localStorage.getItem("devicesLive"), [])
        .map((d: any) => d?.mac ?? d?.deviceMac)
        .filter((value: any) => typeof value === 'string' && value !== '');
      devicesList = devicesList.filter((device: any) =>
        devicesLive.includes(device?.mac ?? device?.deviceMac)
      );
      localStorage.setItem('devicesLive', JSON.stringify(devicesList));
    }

    const devicesWithName = devicesList.filter(
      (device: any) => device.name && device.name.trim() !== ''
    );

    if (onlyBound == 'true') {
      devicesList = devicesWithName;
      localStorage.setItem('devicesList', JSON.stringify(devicesWithName));
    } else {
      localStorage.setItem('devicesList', JSON.stringify(mergedData));
    }

    const groupsCheckboxByMap: any = safeJsonParse<any[]>(localStorage.getItem('groupsCheckboxByMap'), []);
    const groupsCheckedIds = new Set(
      groupsCheckboxByMap
        .filter((item: any) => item.checked != false)
        .map((item: any) => normalizeGroupSelectionId(item.id))
    );

    let devicesToShowInGeoMap: any = [];
    const newSVGMarkers: any = [];
    const scale = transformState?.scale ?? 1;

    devicesList.forEach((device: any) => {
      if (groupsCheckedIds.has(getDeviceGroupSelectionId(device))) {
        const coords = getLatLong(device?.position);
        if (!coords) {
          return;
        }

        devicesToShowInGeoMap.push({
          id: device?.id,
          mac: device?.mac ?? device?.deviceMac,
          name: device?.name,
          groupId: device?.groupId,
          color: device?.groupColor,
          checked: true,
          position: {
            latitude: coords.latitude,
            longitude: coords.longitude,
            elevation: device?.position?.elevation,
            orientation: device?.position?.orientation
          }
        });

        if (!isGeoreferenced) {
          const projectedPoint = projectPointOnCanvas(device?.position, mapImageWidth, mapImageHeight);
          if (!projectedPoint) {
            return;
          }

          const x = Math.round(projectedPoint.x) + 4.5;
          const y = Math.round(projectedPoint.y) - 4;

          // Dimensioni fisse del marker
          const baseMarkerRadius = 16;
          const baseHoleRadius = 12;
          const offset = 4;

          const angleOffset = Math.PI / 6;
          const leftX = x - offset - Math.sin(angleOffset) * baseMarkerRadius;
          const leftY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;
          const rightX = x - offset + Math.sin(angleOffset) * baseMarkerRadius;
          const rightY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;

          newSVGMarkers.push({
            x: x - offset,
            y: y - offset - baseMarkerRadius,
            markerRadius: baseMarkerRadius,
            holeRadius: baseHoleRadius,
            leftX,
            leftY,
            rightX,
            rightY,
            tipX: x - offset,
            tipY: y,
            color: device?.groupColor ?? 'lightgrey',
            deviceMac: device?.mac ?? device?.deviceMac,
            deviceName: device?.name,
            scale: 1 / scale
          });
        }
      }
    });

    localStorage.setItem('devicesChecked', JSON.stringify(devicesToShowInGeoMap));
    setDeviceChecked(devicesToShowInGeoMap);

    // Aggiorna i marker SVG
    setGroupMarkersSVG(newSVGMarkers);

    // Nascondi il loading
    setLoading(false);
    isChangingMapRef.current = false;
  };

  const wakeUpGroupByMapId = async (mapId: any, isGeoreferenced?: any, onlyBound?: any, fromSwitchOnlyBound?: any) => {

    if (mapId == null) return

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      axios.get(`${SERVER_URL}/map/${mapId}/wakeups?isGeoreferenced=${isGeoreferenced}`, { headers })
        .then((res) => {
          const data = applyCachedMqttPositions(res?.data?.data ?? [])

          const devicesList: any = []
          const deviceGroupId: any = []

          const groupsCheckedList: any = safeJsonParse<any[]>(localStorage.getItem('groupsCheckboxByMap'), [])

          data.forEach((device: any) => {
            deviceGroupId.push(device.groupId)

            if (onlyBound) {
              groupsCheckedList.forEach((group: any) => {
                if (group.id == device.groupId) {
                  if (device?.name == '' || device?.name == null) {
                    return
                  }
                }
              })
            }

            devicesList.push(device)
          });

          localStorage.setItem('devicesList', JSON.stringify(devicesList))

          const groupsCheckboxByMap: any = []
          const groupsList = safeJsonParse<any[]>(localStorage.getItem('groupsList'), [])

          // add a checkbox "Select all"
          groupsCheckboxByMap.push({
            id: '0',
            name: localizationService.toLanguageString('map.selectAll', 'NOT_FOUND'),
            color: 'white'
          })

          // add checkbox Not assigned
          groupsCheckboxByMap.push({
            id: null,
            name: localizationService.toLanguageString('map.noGroup', 'NOT_FOUND'),
            color: 'lightgrey'
          })


          // populate the checkbox group
          groupsList?.forEach((group: any) => {
            if (deviceGroupId.includes(group.id)) {
              groupsCheckboxByMap.push({
                id: group?.id,
                name: group?.name,
                color: group?.color,
              })
            }
          })

          groupsCheckboxByMap.forEach((group: any) => {
            group.checked = true
          });

          localStorage.setItem('groupsCheckboxByMap', JSON.stringify(groupsCheckboxByMap));

          if (!fromSwitchOnlyBound) {
            setGroupsCheckboxByMap(groupsCheckboxByMap)
          }

          // Process markers directly with data already fetched
          processMarkersFromData(data, mapId, isGeoreferenced);

          if (alarmChecked) changeAlarm(alarmChecked.checked)

        })
        .catch((error) => {
          setLoading(false);
          isChangingMapRef.current = false;
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }
  }

  const refreshGroupPositionsInMap = async (mapId: any, isGeoreferenced?: any) => {

    setLoading(true)

    setShowTooltip(false)

    if (mapId == null || mapId == '' || mapId == undefined) return

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      await axios.get(`${SERVER_URL}/map/${mapId}/wakeups?isGeoreferenced=${isGeoreferenced}`, { headers })
        .then((res) => {

          const data = applyCachedMqttPositions(res?.data?.data ?? [])

          let devicesList: any = data

          const isLive = localStorage.getItem('liveStatusMap')
          const onlyBound = localStorage.getItem('onlyBoundMap')

          const currDeviceMacFromSynoptic = localStorage.getItem('deviceMacFromSynoptic');

          if (currDeviceMacFromSynoptic != null && deviceMapIdFromSynoptic == mapId) {
            devicesList = devicesList.filter((device: any) =>
              (device?.deviceMac ?? device?.mac) == currDeviceMacFromSynoptic
            )
          }

          if (isLive == 'true') {

            const minutesBefore: any = localStorage.getItem('statusDevice') ?? 75 // default value in hour    

            let liveDevicesToShowInMap: any = []
            let dateBeforeMinutes: any = ''

            devicesList?.map((device: any) => {
              if (device.dateTime == null || device.dateTime == 0) {
                return
              } else {
                dateBeforeMinutes = getDateRange(minutesBefore)
                device.dateTime = convertDateTime(device?.dateTime);
                const localTimeString = device.dateTime.toLocaleString(languageId, {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  //hour12: true // Force 12-hour format with AM/PM
                });
                if (compareDateTimes(localTimeString, dateBeforeMinutes) == 1) {
                  liveDevicesToShowInMap.push(device)
                }
              }
            })

            setDeviceChecked(liveDevicesToShowInMap)
            localStorage.setItem('devicesLive', JSON.stringify(liveDevicesToShowInMap))

            const devicesLive = safeJsonParse<any[]>(localStorage.getItem("devicesLive"), [])
              .map((d: any) => d?.deviceMac ?? d?.mac)
              .filter((value: any) => typeof value === 'string' && value !== '');
            devicesList = devicesList.filter((device: any) =>
              devicesLive.includes(device?.deviceMac ?? device?.mac)
            );
            localStorage.setItem('devicesLive', JSON.stringify(devicesList))
          }

          const devicesWithName = devicesList.filter(
            (device: any) => device.name && device.name.trim() !== ''
          );

          if (onlyBound == 'true') {
            devicesList = devicesWithName
            localStorage.setItem('devicesList', JSON.stringify(devicesWithName))
          } else {
            localStorage.setItem('devicesList', JSON.stringify(data))
          }

          const groupsCheckboxByMap: any = safeJsonParse<any[]>(localStorage.getItem('groupsCheckboxByMap'), []);
          const groupsCheckedIds = new Set(
            groupsCheckboxByMap
              .filter((item: any) => item.checked != false)
              .map((item: any) => normalizeGroupSelectionId(item.id))
          );

          let devicesToShowInGeoMap: any = []
          const newSVGMarkers: any = []
          const scale = transformState?.scale ?? 1;

          devicesList.forEach((device: any) => {
            if (groupsCheckedIds.has(getDeviceGroupSelectionId(device))) {
              const coords = getLatLong(device?.position);
              if (!coords) {
                return;
              }

              devicesToShowInGeoMap.push({
                id: device?.id,
                mac: device?.deviceMac ?? device?.mac,
                name: device?.name,
                groupId: device?.groupId,
                color: device?.groupColor,
                checked: true,
                position: {
                  latitude: coords.latitude,
                  longitude: coords.longitude,
                  elevation: device?.position?.elevation,
                  orientation: device?.position?.orientation
                }
              })

              if (!isGeoreferenced) {
                const projectedPoint = projectPointOnCanvas(device?.position, mapImageWidth, mapImageHeight);
                if (!projectedPoint) {
                  return;
                }

                // Coordinate sulla mappa (il TransformComponent si occupa della trasformazione)
                const x = Math.round(projectedPoint.x) + 4.5;
                const y = Math.round(projectedPoint.y) - 4;

                // Dimensioni fisse del marker
                const baseMarkerRadius = 16;
                const baseHoleRadius = 12;
                const offset = 4;

                // Calcola i punti dove la punta si attacca al cerchio (ai lati)
                const angleOffset = Math.PI / 6; // 30 gradi
                const leftX = x - offset - Math.sin(angleOffset) * baseMarkerRadius;
                const leftY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;
                const rightX = x - offset + Math.sin(angleOffset) * baseMarkerRadius;
                const rightY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;

                // Create SVG marker data
                newSVGMarkers.push({
                  x: x - offset,
                  y: y - offset - baseMarkerRadius,
                  markerRadius: baseMarkerRadius,
                  holeRadius: baseHoleRadius,
                  leftX,
                  leftY,
                  rightX,
                  rightY,
                  tipX: x - offset,
                  tipY: y,
                  color: device?.groupColor ?? 'lightgrey',
                  deviceMac: device?.deviceMac ?? device?.mac,
                  deviceName: device?.name,
                  scale: 1 / scale
                });
              }
            }
          })
          localStorage.setItem('devicesChecked', JSON.stringify(devicesToShowInGeoMap))
          setDeviceChecked(devicesToShowInGeoMap)

          setGroupMarkersSVG(newSVGMarkers)
          setLoading(false)
          isChangingMapRef.current = false
        })
        .catch((error) => {
          setLoading(false);
          isChangingMapRef.current = false;
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }
    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }
  }

  const changeZone = (ev: any, zoneId: any) => {
    let checked: any = ev.target.element.checked;
    let zonesCheckboxListByMap: any[] = zonesCheckboxByMap
    let zonesChecked: any[] = localStorage.getItem('zonesChecked')
      ? safeJsonParse<any[]>(localStorage.getItem('zonesChecked'), [])
      : []

    let zoneCtx: any;

    //manage checkbox "selectall"
    if (zoneId == 0) {
      if (!checked) {
        const updatedZones = zonesCheckboxByMap.map((zone: any) => ({
          ...zone,
          checked: false
        }));

        setZoneCheckedList(updatedZones);
        setZonesCheckboxByMap(updatedZones);
        zonesChecked = updatedZones;

        if (zoneCanvas.current != null || currentZoneCanvas.current != null) {
          zoneCtx = zoneCanvas?.current?.getContext("2d") ?? currentZoneCanvas?.current?.getContext("2d");
          zoneCtx.clearRect(0, 0, zoneCanvas.current.width, zoneCanvas.current.height);
        }

        localStorage.setItem('zonesChecked', JSON.stringify([]));
      } else {
        const updatedZones = zonesCheckboxListByMap.map((zone: any) => ({
          ...zone,
          checked: true
        }));

        setZoneCheckedList(updatedZones);
        setZonesCheckboxByMap(updatedZones);
        zonesChecked = updatedZones;

        localStorage.setItem('zonesChecked', JSON.stringify(updatedZones));
      }
    }

    //manage single checkbox
    if (zoneId != 0) {
      const nextZones = zonesCheckboxByMap.map((zone: any) =>
        zone.id == zoneId ? { ...zone, checked } : zone
      );

      if (!checked) {
        zonesChecked = zonesChecked.filter(item => item.id !== zoneId);
      } else {
        const selectedZone = nextZones.find((zone: any) => zone.id == zoneId);
        if (selectedZone && !zonesChecked.some((zone: any) => zone.id === zoneId)) {
          zonesChecked.push(selectedZone)
        }
      }

      setZonesCheckboxByMap(nextZones)
      setZoneCheckedList(zonesChecked)
      localStorage.setItem('zonesChecked', JSON.stringify(zonesChecked));

      const updatedZones = nextZones.map((zone: any) => ({ ...zone }));

      const allCheckedExceptZero = updatedZones.filter((z: any) => z.id !== '0').every((z: any) => z.checked === true);
      const allUncheckedExceptZero = updatedZones.filter((z: any) => z.id !== '0').some((z: any) => z.checked === false);

      if (allCheckedExceptZero) {
        const zoneZero = updatedZones.find((z: any) => z.id === '0');
        if (zoneZero) {
          zoneZero.checked = true; // deseleziona zoneId = 0
        }
        setZonesCheckboxByMap(updatedZones);
      }
      if (allUncheckedExceptZero) {
        const zoneZero = updatedZones.find((z: any) => z.id === '0');
        if (zoneZero) {
          zoneZero.checked = false; // deseleziona zoneId = 0
        }
        setZonesCheckboxByMap(updatedZones);
      }

    }

    if (zoneCanvas.current != null || currentZoneCanvas.current != null) {
      zoneCtx = zoneCanvas?.current?.getContext("2d") ?? currentZoneCanvas?.current?.getContext("2d");
      zoneCtx.clearRect(0, 0, zoneCanvas.current.width, zoneCanvas.current.height)
    }

    // re-render the zone canvas after deleting a zone deselected
    if (zonesChecked.length > 0 && !mapGeoreferenced) {
      zonesChecked.forEach((zone: any) => {
        if (zone.id != 0 && zone.checked) {
          zoneCtx.beginPath();
          zoneCtx.fillStyle = zone.color
          zoneCtx.moveTo(zoneCanvas.current.width * zone.points[0].longitude, zoneCanvas.current.height - zoneCanvas.current.height * zone.points[0].latitude);
          zone.points.forEach((points: any) => {
            zoneCtx.lineTo(zoneCanvas.current.width * points.longitude, zoneCanvas.current.height - (zoneCanvas.current.height * points.latitude));
          });
          zoneCtx.globalAlpha = 0.8;
          zoneCtx.fill();
        }
      });
    }

  };

  const changeGroup = (ev?: any, groupId?: any, isMapGeoreferenced?: boolean) => {

    let checked: any = ev?.target?.element?.checked ?? ev;

    let devicesList: any = safeJsonParse<any[]>(localStorage.getItem('devicesList'), [])
    let groupsCheckboxByMap: any = safeJsonParse<any[]>(localStorage.getItem('groupsCheckboxByMap'), [])

    let groupsCheckboxListByMap: any = groupsCheckboxByMap

    let devicesChecked: any = []
    let groupCheckedLive: any = []
    if (localStorage.getItem('liveStatusMap') == 'true') {
      groupCheckedLive = safeJsonParse<any[]>(localStorage.getItem('devicesLive'), [])
    } else {
      devicesChecked = localStorage.getItem('devicesChecked') != null && localStorage.getItem('devicesChecked') != '[]'
        ? safeJsonParse<any[]>(localStorage.getItem('devicesChecked'), devicesList)
        : devicesList
    }

    //manage checkbox "selectall"
    if (groupId == 0) {
      if (!checked) {
        devicesChecked = []
        groupsCheckboxListByMap?.forEach((group: any) => {
          group.checked = false
        });
      } else {
        if (liveStatusMap == true) {
          groupsCheckboxListByMap?.map((group: any) =>
            group.checked = false
          );
        }
        groupsCheckboxListByMap?.forEach((group: any) => {
          group.checked = true
        });
        devicesChecked = devicesList
      }
      setGroupsCheckboxByMap(groupsCheckboxListByMap)
      localStorage.setItem('devicesChecked', JSON.stringify(devicesChecked))
      localStorage.setItem('groupsCheckboxByMap', JSON.stringify(groupsCheckboxListByMap));
    }

    //manage single checkbox
    if (groupId != 0) {
      if (devicesList.length > 0) {
        const groupIds = devicesList.map((device: any) => device.groupId);
        const exists = groupIds.includes(groupId);
        if (exists) {
          devicesList.forEach((group: any) => {
            if (group?.groupId == groupId) {
              groupsCheckboxByMap.forEach((g: any) => {
                if (g.id === groupId) {
                  g.checked = checked;
                }
              });
            }
          });
        } else {
          groupsCheckboxByMap.forEach((group: any) => {
            if (group.id == groupId) {
              group.checked = checked
            }
          })
        }
      } else {
        const groupIds = groupsCheckboxByMap.map((device: any) => device.groupId);
        const exists = groupIds.includes(groupId);
        if (exists) {
          groupsCheckboxByMap.forEach((group: any) => {
            if (group?.groupId == groupId) {
              group.checked = checked;
            }
          });
        } else {
          groupsCheckboxByMap.forEach((group: any) => {
            if (group.id == groupId) {
              group.checked = checked
            }
          })
        }
      }

      setGroupsCheckboxByMap(groupsCheckboxByMap)

      const updatedGroups = groupsCheckboxListByMap.map((group: any) => ({ ...group }));

      const allCheckedExceptZero = updatedGroups.filter((z: any) => z.id !== '0').every((z: any) => z.checked === true);
      const allUncheckedExceptZero = updatedGroups.filter((z: any) => z.id !== '0').some((z: any) => z.checked === false);

      if (allCheckedExceptZero) {
        const zoneZero = updatedGroups.find((z: any) => z.id === '0');
        if (zoneZero) {
          zoneZero.checked = true;
        }
      }
      if (allUncheckedExceptZero) {
        const zoneZero = updatedGroups.find((z: any) => z.id === '0');
        if (zoneZero) {
          zoneZero.checked = false;
        }
      }
      groupsCheckboxListByMap = updatedGroups;
      setGroupsCheckboxByMap(updatedGroups);

      localStorage.setItem('groupsCheckboxByMap', JSON.stringify(updatedGroups));
    }

    const checkedIds = new Set(
      groupsCheckboxListByMap
        .filter((item: any) => item.checked != false)
        .map((item: any) => normalizeGroupSelectionId(item.id))
    );
    let updatedGroupChecked: any = []

    if (!isMapGeoreferenced) {

      if (liveStatusMap == true) {
        const groupIds = new Set(groupsCheckboxByMap.filter((g: any) => g.checked === true).map((g: any) => g.id));
        if (localStorage.getItem('onlyBoundMap') == 'true') {
          groupCheckedLive = groupCheckedLive.filter((item: any) =>
            item.name != ''
          )
        }
        const commonItems = groupCheckedLive.filter((item: any) =>
          groupIds.has(item.groupId)
        );

        commonItems.forEach((device: any) => {
          updatedGroupChecked.push(device)
        });
      }
      else {
        // Filtra i dispositivi in base ai gruppi selezionati
        if (devicesList.length > 0 && !mapGeoreferenced) {
          devicesList.forEach((device: any) => {
            const deviceGroupId = getDeviceGroupSelectionId(device);
            if (deviceMacFromSynoptic != null && device.deviceMac == deviceMacFromSynoptic && checkedIds.has(deviceGroupId)) {
              if (localStorage.getItem('onlyBoundMap') == 'true') {
                if (device?.name == '' || device?.name == null) {
                  return
                }
              }
              updatedGroupChecked.push(device)
            }
            else if (deviceMacFromSynoptic == null && checkedIds.has(deviceGroupId)) {
              if (localStorage.getItem('onlyBoundMap') == 'true') {
                if (device?.name == '' || device?.name == null) {
                  return
                }
              }
              updatedGroupChecked.push(device)
            }
          });
        }
      }
      setDeviceChecked(updatedGroupChecked)
      localStorage.setItem('devicesChecked', JSON.stringify(updatedGroupChecked));

      // Aggiorna i marker SVG direttamente dai dispositivi filtrati
      const scale = transformState?.scale ?? 1;
      const newSVGMarkers: any = [];
      const offset = 4;

      updatedGroupChecked.forEach((device: any) => {
        const projectedPoint = projectPointOnCanvas(device?.position, mapImageWidth, mapImageHeight);
        if (!projectedPoint) {
          return;
        }

        const x = Math.round(projectedPoint.x) + 4.5;
        const y = Math.round(projectedPoint.y) - 4;

        // Dimensioni fisse del marker
        const baseMarkerRadius = 16;
        const baseHoleRadius = 12;

        const angleOffset = Math.PI / 6;
        const leftX = x - offset - Math.sin(angleOffset) * baseMarkerRadius;
        const leftY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;
        const rightX = x - offset + Math.sin(angleOffset) * baseMarkerRadius;
        const rightY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;

        newSVGMarkers.push({
          x: x - offset,
          y: y - offset - baseMarkerRadius,
          markerRadius: baseMarkerRadius,
          holeRadius: baseHoleRadius,
          leftX,
          leftY,
          rightX,
          rightY,
          tipX: x - offset,
          tipY: y,
          color: device?.color ?? device?.groupColor ?? 'lightgrey',
          deviceMac: device?.deviceMac,
          deviceName: device?.name,
          scale: 1 / scale
        });
      });

      setGroupMarkersSVG(newSVGMarkers);
    }
    else {
      if (devicesList.length > 0 && mapGeoreferenced) {
        devicesList.forEach((device: any) => {
          if (checkedIds.has(getDeviceGroupSelectionId(device))) {
            if (localStorage.getItem('onlyBoundMap') == 'true') {
              if (device?.name == '' || device?.name == null) {
                return
              }
            }
            updatedGroupChecked.push(device)
          }
        });
      }
      setDeviceChecked(updatedGroupChecked)
      localStorage.setItem('devicesChecked', JSON.stringify(updatedGroupChecked));
    }
  };

  // toggle checkbox beacon and render beacon in map as SVG
  const updateBeaconMarkers = (beaconList: any[]) => {
    if (!Array.isArray(beaconList) || mapImageWidth === 0 || mapImageHeight === 0) {
      setBeaconMarkersSVG([]);
      return;
    }

    const scale = transformState?.scale ?? 1;
    const newBeaconMarkers: any[] = [];

    beaconList.forEach((beacon: any) => {
      if (!beacon?.checked || beacon?.id == '0') {
        return;
      }

      const projectedPoint = projectPointOnCanvas(beacon, mapImageWidth, mapImageHeight);
      if (!projectedPoint) {
        return;
      }

      newBeaconMarkers.push({
        x: projectedPoint.x,
        y: projectedPoint.y,
        color: beacon.color,
        name: beacon.name,
        type: beacon.type,
        id: beacon.id,
        scale: 1 / scale
      });
    });

    setBeaconMarkersSVG(newBeaconMarkers);
  };

  const changeCheckboxBeacon = (ev: any, beaconIdSelected?: any, fromBeaconGrid?: []) => {
    const checked: boolean = Boolean(ev?.target?.element?.checked ?? ev);
    const sourceList: any[] = (Array.isArray(fromBeaconGrid) && fromBeaconGrid.length > 0)
      ? fromBeaconGrid
      : allBeacons;

    if (!Array.isArray(sourceList) || sourceList.length === 0) {
      return;
    }

    let updatedBeacons = sourceList.map((beacon: any) => {
      if (beaconIdSelected == '0') {
        if (beacon.id == '0') {
          return { ...beacon, checked };
        }
        return { ...beacon, checked };
      }

      if (beacon.id == beaconIdSelected) {
        return { ...beacon, checked };
      }

      return { ...beacon };
    });

    const selectableBeacons = updatedBeacons.filter((b: any) => b.id != '0');
    const allCheckedExceptZero = selectableBeacons.length > 0 && selectableBeacons.every((b: any) => b.checked === true);

    updatedBeacons = updatedBeacons.map((beacon: any) => {
      if (beacon.id == '0') {
        return { ...beacon, checked: allCheckedExceptZero };
      }
      return beacon;
    });

    const checkedBeacons = updatedBeacons.filter((beacon: any) => beacon.checked && beacon.id != '0');

    setAllBeacons(updatedBeacons);
    setListBeaconsOn(customFilterBeacons(updatedBeacons, beaconFilter));
    setSelectedBeacons(checkedBeacons);

    if (fromBeaconGrid != null) {
      setBeaconsFromGrid(updatedBeacons);
    }

    localStorage.setItem('beaconsByMapId', JSON.stringify(updatedBeacons));
    updateBeaconMarkers(updatedBeacons);
  };

  // Gestisce beaconId da localStorage
  useEffect(() => {
    if (beaconId && mapIdByBeacon && allBeacons.length > 0) {
      const beaconFromStorage: any = allBeacons.find((b: any) => b.id == beaconId);
      if (beaconFromStorage && !beaconFromStorage.checked) {
        const updatedBeacons = allBeacons.map((beacon: any) =>
          beacon.id == beaconId ? { ...beacon, checked: true } : beacon
        );
        setAllBeacons(updatedBeacons as any);
        setSelectedBeacons([{ ...beaconFromStorage, checked: true }]);
        changeCheckboxBeacon({ target: { element: { checked: true } } }, beaconId);
      }
    }
  }, [beaconId, mapIdByBeacon, allBeacons]);

  const onChangeBeacon = (event: any) => {
    const newSelectedIds = new Set(
      (event?.value || [])
        .map((beacon: any) => beacon?.id)
        .filter((id: any) => id != null && id != '0')
    );

    let updatedBeacons = allBeacons.map((beacon: any) => {
      if (beacon.id == '0') {
        return { ...beacon };
      }

      return {
        ...beacon,
        checked: newSelectedIds.has(beacon.id)
      };
    });

    const selectableBeacons = updatedBeacons.filter((b: any) => b.id != '0');
    const allCheckedExceptZero = selectableBeacons.length > 0 && selectableBeacons.every((b: any) => b.checked === true);

    updatedBeacons = updatedBeacons.map((beacon: any) => {
      if (beacon.id == '0') {
        return { ...beacon, checked: allCheckedExceptZero };
      }
      return beacon;
    });

    const checkedBeacons = updatedBeacons.filter((beacon: any) => beacon.checked && beacon.id != '0');

    setAllBeacons(updatedBeacons);
    setListBeaconsOn(customFilterBeacons(updatedBeacons, beaconFilter));
    setSelectedBeacons(checkedBeacons);
    localStorage.setItem('beaconsByMapId', JSON.stringify(updatedBeacons));
    updateBeaconMarkers(updatedBeacons);

    if (localStorage.getItem('beaconId') != null) {
      localStorage.removeItem('beaconId');
      localStorage.removeItem('mapIdByBeacon');
    }

    if (newSelectedIds.size === 0) {
      localStorage.removeItem('beaconId');
      localStorage.removeItem('mapIdByBeacon');
    }
  };

  const customFilterBeacons = (data: any[], filterText: string) => {
    const lowerFilter = filterText.toLowerCase();
    return data.filter(item =>
      (item.name && item.name.toLowerCase().includes(lowerFilter)) ||
      (item.id && item.id.toString().toLowerCase().includes(lowerFilter))
    );
  };

  const filterChange2 = (event: any) => {
    const filterText = event?.filter?.value || '';
    setBeaconFilter(filterText);
    if (!filterText) {
      // reset filter
      setListBeaconsOn(allBeacons);
      return;
    }
    const filtered = filterText ? customFilterBeacons(allBeacons, filterText) : allBeacons;
    setListBeaconsOn(filtered);

  };

  const tagRender = (tagData: any, _tag: React.ReactElement) => {
    const item = tagData?.data[0];
    return (
      <span className="maps-tag-item">
        {item?.id != 0 ? (
          <>
            {item.id} <br /> {item.name}
          </>
        ) : (
          item.name
        )}
      </span>
    );
  };

  const beaconsRenderList = (li: React.ReactElement<HTMLLIElement>, itemProps: any) => {
    const item = itemProps?.dataItem;
    if (!item) return li;

    return (
      <div
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          name={`${item?.id} - ${item?.name}`}
          size={'small'}
          tabIndex={-1}
          onChange={(e: any) => {
            changeCheckboxBeacon(e, item?.id)
            e?.target?.element?.blur?.();
          }}
          label={
            <div className="maps-beacon-option-label">
              {item?.id != 0 ? item?.name : ''}
            </div>
          }
          checked={item?.checked}
        >
          <label className="checkbox-label-beacon maps-marker-chip" style={markerColorStyle(item?.color)} />
          {
            item?.id == 0 &&
            <label className="maps-beacon-meta">{item?.name}</label>
          }
          <label className="maps-beacon-meta">{item?.id != 0 ? item?.id : ''}</label>
        </Checkbox>
      </div>
    );
  };

  const changeAlarm = (ev: any) => {
    let checked: any = ev?.target?.element?.checked ?? ev;

    const alarmCtx = alarmCanvas?.current?.getContext("2d");
    const alarmWidth = 30;
    const alarmHeight = 18;

    if (alarmChecked != null) {
      const updatedAlarmChecked = {
        ...alarmChecked,
        checked: Boolean(checked)
      };

      setAlarmChecked(updatedAlarmChecked);

      if (checked) {
        setAlarmToggle(true)
        localStorage.setItem("alarmInfoSelected", JSON.stringify(updatedAlarmChecked));
      }
      else {
        if (alarmChecked?.checked == true && alarmCtx != null && alarmCanvas?.current) {
          alarmCtx.clearRect(0 - alarmWidth / 2, 0 - alarmHeight / 2, alarmCanvas.current.width + alarmWidth / 2, alarmCanvas.current.height + alarmHeight / 2)
        }
        setAlarmToggle(false)
        localStorage.removeItem('alarmInfoSelected');
      }

      if (updatedAlarmChecked.checked && alarmCtx != null && alarmCanvas?.current) {
        alarmCtx.beginPath();
        alarmCtx.fillStyle = 'red'
        alarmCtx.lineWidth = 2;
        alarmCtx.strokeStyle = 'black';

        const width = alarmCanvas.current.width
        const height = alarmCanvas.current.height

        const x = width * updatedAlarmChecked?.info?.position?.longitude;
        const y = height - height * updatedAlarmChecked?.info?.position?.latitude;
        alarmCtx.fillRect(x - alarmWidth / 2, y - alarmHeight / 2, alarmWidth, alarmHeight);
        alarmCtx.strokeRect(x - alarmWidth / 2, y - alarmHeight / 2, alarmWidth, alarmHeight);
      }

    }
  };

  const getAlarmPositionByWakeupId = async (wakeupId: any, mapsList: any) => {

    setLoading(true);

    let languageId = localStorage.getItem('languageId');

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

      await axios.get(`${SERVER_URL}/wakeup/${wakeupId}`, { headers })
        .then((res) => {
          const data = res?.data.data

          setAlarmInfo(data)

          getMapByPosition(data.position.longitude, data.position.latitude, data.position.elevation, data.position.orientation, mapsList)

          setLoading(false);

        }).catch(() => {
          setLoading(false);
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  const getAlarmRelativePositionsById = async (alarmId: any, mapId: any) => {

    setLoading(true);

    let languageId = localStorage.getItem('languageId');

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

      await axios.get(`${SERVER_URL}/alarm/getAlarmRelativePositionsById?alarmId=${alarmId}&mapId=${mapId}`, { headers })
        .then((res) => {
          const data = res?.data.data

          setAlarmInfo(data)
          setAlarmChecked(data)

          setLoading(false);

        }).catch(() => {
          setLoading(false);
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  useEffect(() => {
    changeAlarm(alarmToggle)
  }, [alarmToggle])

  const mapRef: any = useRef(null);

  const getDateRange = (minutes: any) => {
    // Create a Date object from the input string
    const currentDate: any = new Date();

    // 2. Calculate milliseconds to subtract:
    const millisecondsToSubtract = minutes * 60 * 1000; // minutes * seconds * milliseconds

    // 3. Subtract the milliseconds:
    currentDate.setTime(currentDate?.getTime() - millisecondsToSubtract);

    const localTimeString = currentDate.toLocaleString(languageId, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      //hour12: true // Force 12-hour format with AM/PM
    });

    return localTimeString
  }

  const convertToDate = (dateString: string, isItalian: boolean = false): Date | null => {
    if (!dateString) return null;

    try {
      const cleanStr = dateString.replace(/,/g, '').trim();
      const parts = cleanStr.split(' ');

      let day: number, month: number, year: number;
      let hours: number = 0, minutes: number = 0, seconds: number = 0;

      if (isItalian) {
        // Formato italiano: "dd/MM/yyyy HH:mm:ss"
        const [datePart, timePart] = parts;
        if (!datePart || !timePart) return null;

        [day, month, year] = datePart.split('/').map(Number);
        [hours, minutes, seconds] = timePart.split(':').map(Number);
      } else {
        // Formato inglese: "MM/dd/yyyy hh:mm:ss AM|PM"
        if (parts.length < 3) return null;

        const [datePart, timePart, ampm] = parts;
        [month, day, year] = datePart.split('/').map(Number);
        [hours, minutes, seconds] = timePart.split(':').map(Number);

        if (ampm === 'PM' && hours < 12) hours += 12;
        if (ampm === 'AM' && hours === 12) hours = 0;
      }

      if ([day, month, year, hours, minutes, seconds].some(n => isNaN(n))) return null;

      const date = new Date(year, month - 1, day, hours, minutes, seconds);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  const compareDateTimes = (date1Str: any, date2Str: any) => {

    let currLanguage: any = languageId
    if (currLanguage == 'it-IT') {
      currLanguage = true
    } else {
      currLanguage = false
    }
    // Parse the dates into Date objects    
    const date1: any = convertToDate(date1Str, currLanguage);
    const date2: any = convertToDate(date2Str, currLanguage);

    // Compare the milliseconds
    if (date1?.getTime() < date2?.getTime()) {
      return -1; // date1 is earlier
    } else if (date1?.getTime() > date2?.getTime()) {
      return 1; // date1 is later
    } else {
      return 0; // dates are equal
    }
  }

  const convertDateTime = (dateTime: any) => {

    if (dateTime == null) {
      return '-'
    }

    const [datePart, timePart] = dateTime?.split(' ');
    const [day, month, year] = datePart?.split('/');
    const [hour, minute, seconds] = timePart?.split(':');

    const date = new Date(Date.UTC(year, month - 1, day, hour, minute, seconds))

    return date
  }

  const onToggleLive = (e: any) => {

    const liveStatusMap: any = e.value ?? e
    setLiveStatus(liveStatusMap)
    localStorage.setItem('liveStatusMap', liveStatusMap)

    const onlyBoundMap = localStorage.getItem('onlyBoundMap')

    const storedGroupsChecked: any = localStorage.getItem('devicesChecked')
    const storedDevicesList: any = localStorage.getItem('devicesList')
    let currentMapId: any = parseStoredMapId(localStorage.getItem('mapId'))
    const groupsChecked = storedGroupsChecked != '' ? safeJsonParse<any[]>(storedGroupsChecked, []) : []

    if (liveStatusMap) {
      const minutesBefore: any = localStorage.getItem('statusDevice') ?? 75 // default value in hour    

      const devicesList: any = safeJsonParse<any[]>(localStorage.getItem('devicesList'), [])

      let liveDevicesToShowInMap: any = []
      let dateBeforeMinutes: any = ''

      if (groupsChecked?.length == 0) return

      devicesList?.map((device: any) => {
        if (device.dateTime == null || device.dateTime == 0) {
          return
        } else {
          dateBeforeMinutes = getDateRange(minutesBefore)
          device.dateTime = convertDateTime(device?.dateTime);
          const localTimeString = device.dateTime.toLocaleString(languageId, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            //hour12: true // Force 12-hour format with AM/PM
          });
          if (compareDateTimes(localTimeString, dateBeforeMinutes) == 1) {
            liveDevicesToShowInMap.push(device)
          }
        }
      })

      if (onlyBoundMap == 'true') {
        liveDevicesToShowInMap = liveDevicesToShowInMap.filter((device: any) => device.name !== '');
      }
      setDeviceChecked(liveDevicesToShowInMap)
      localStorage.setItem('devicesLive', JSON.stringify(liveDevicesToShowInMap))
      //wakeUpGroupByMapId(currentMapId, liveStatusMap)

    } else {
      const devicesToShowInMap: any = safeJsonParse<any[]>(storedDevicesList, [])
      setDeviceChecked(devicesToShowInMap)
      localStorage.setItem('devicesChecked', JSON.stringify(devicesToShowInMap))
    }

    if (currentMapId != null) {
      refreshGroupPositionsInMap(currentMapId, mapGeoreferenced)
    }
  }

  const onToggleOnlyBound = (e: any) => {
    const onlyBound: any = e.value
    setOnlyBound(onlyBound)
    localStorage.setItem('onlyBoundMap', JSON.stringify(onlyBound))

    const groupsCheckboxByMap = safeJsonParse<any[]>(localStorage.getItem('groupsCheckboxByMap'), [])

    if (groupsCheckboxByMap.length > 0 && selectedMap?.id != null) {
      refreshGroupPositionsInMap(selectedMap.id, mapGeoreferenced)
    }
  }

  const onToggleGeoStatic = (e: any) => {

    const toggleGeoStatic: any = e.value
    localStorage.setItem('mapGeoreferenced', JSON.stringify(toggleGeoStatic))
    setToggleGeoStatic(toggleGeoStatic)
    setMapGeoreferenced(toggleGeoStatic)

    const currMapSelected: any = selectedMap || '[]'
    const currMapId = currMapSelected.id
    renderMap(currMapSelected)

    if (toggleGeoStatic) {
      localStorage.removeItem('beaconId');
      localStorage.removeItem('mapIdByBeacon');
      localStorage.removeItem('beaconsByMapId');
      changeMap(currMapSelected.name, false)
      refreshGroupPositionsInMap(currMapId, true)
    } else {
      getZoneList(currMapId, false)
      getBeaconList(currMapId, true, false, false, null, false)
      changeMap(currMapSelected.name, false)
      refreshGroupPositionsInMap(currMapId, false)
      if (alarmInfoSelected != null) {
        getAlarmRelativePositionsById(alarmInfoSelected.id, currMapId)
      }
    }
  }

  const onToggleGrouping = (e: any) => {
    const groupingValue = typeof e?.value === 'boolean'
      ? e.value
      : (typeof e?.target?.checked === 'boolean' ? e.target.checked : !groupingEnabled);
    setGroupingEnabled(groupingValue);
    localStorage.setItem('groupingEnabledMap', groupingValue ? 'true' : 'false');
  }

  useEffect(() => {
    if (lastMouseEvent) {
      handleUnifiedTooltip(lastMouseEvent);
    }
  }, [transformState]); // ogni volta che fai pan/zoom

  // Metodo unificato per gestire tutti i tooltip (marker SVG, beacon, alarm, devices)
  const handleUnifiedTooltip = (e: any) => {
    setLastMouseEvent(e);

    if (!transformContentRef.current) return;

    const { scale } = transformState;

    // Bounding box del contenitore trasformato
    const rect = transformContentRef.current.getBoundingClientRect();

    // Mouse position relativa al contenitore trasformato
    const mouseX = (e.clientX - rect.left) / scale;
    const mouseY = (e.clientY - rect.top) / scale;

    let tooltipStr = '';
    let found = false;
    let isBeaconTooltip = false;
    let isDeviceTooltip = false;

    // PRIORITÃ€ 1: Verifica prima i marker SVG (hanno la precedenza)
    for (const marker of groupMarkersSVG) {
      const markerRadius = marker.markerRadius;
      const markerScale = marker.scale || 1;
      const tipX = marker.tipX;
      const tipY = marker.tipY;
      const circleX = marker.x;
      const circleY = marker.y;

      // Il marker viene scalato attorno al punto (marker.x, marker.tipY)
      // Trasforma le coordinate del mouse nello spazio scalato del marker
      const dx = mouseX - marker.x;
      const dy = mouseY - marker.tipY;

      // Inverti la scala per ottenere le coordinate del mouse nello spazio del marker originale
      const localMouseX = marker.x + (dx / markerScale);
      const localMouseY = marker.tipY + (dy / markerScale);

      // Ora verifica se localMouse Ã¨ dentro il marker con dimensioni originali
      const distToCircle = Math.hypot(localMouseX - circleX, localMouseY - circleY);

      // Verifica il triangolo nello spazio originale
      const triangleHeight = tipY - circleY;
      const triangleBaseWidth = markerRadius * 1.6;
      const isInTriangle = localMouseY >= circleY &&
        localMouseY <= tipY &&
        localMouseX >= (tipX - (triangleBaseWidth / 2) * ((tipY - localMouseY) / triangleHeight)) &&
        localMouseX <= (tipX + (triangleBaseWidth / 2) * ((tipY - localMouseY) / triangleHeight));

      if (distToCircle <= markerRadius || isInTriangle) {
        const deviceData = resolveDeviceTooltipData(marker.deviceMac, {
          deviceMac: marker.deviceMac,
          name: marker.deviceName,
          groupColor: marker.color,
        });
        tooltipStr = buildDeviceTooltipHtml(deviceData, getDeviceTooltipLabels(localizationService), languageId);
        found = true;
        isDeviceTooltip = true;
        break;
      }
    }

    // Se non ha trovato marker SVG dei dispositivi, cerca beacon SVG
    if (!found) {
      // Beacon SVG tooltips
      for (const beacon of beaconMarkersSVG) {
        const beaconWidth = 30;
        const beaconHeight = 18;
        const beaconScale = beacon.scale || 1;

        // Il beacon viene scalato attorno al suo centro (beacon.x, beacon.y)
        const dx = mouseX - beacon.x;
        const dy = mouseY - beacon.y;

        // Inverti la scala per ottenere le coordinate del mouse nello spazio del beacon originale
        const localMouseX = beacon.x + (dx / beaconScale);
        const localMouseY = beacon.y + (dy / beaconScale);

        // Verifica se il mouse Ã¨ dentro il rettangolo del beacon
        const isInBeacon = localMouseX >= (beacon.x - beaconWidth / 2) &&
          localMouseX <= (beacon.x + beaconWidth / 2) &&
          localMouseY >= (beacon.y - beaconHeight / 2) &&
          localMouseY <= (beacon.y + beaconHeight / 2);

        if (isInBeacon) {
          tooltipStr += `<div>${localizationService.toLanguageString('map.name', 'NOT_FOUND')}: ${beacon.name}</div><div>${localizationService.toLanguageString('map.type', 'NOT_FOUND')}: ${beacon.type}</div>`;
          found = true;
          isBeaconTooltip = true;
          break;
        }
      }

      // Alarm tooltips
      if (!found && alarmChecked != null && alarmChecked.checked) {
        const radius = 10;
        const groupX = alarmChecked?.info?.position?.longitude * mapImageWidth;
        const groupY = mapImageHeight - alarmChecked?.info?.position?.latitude * mapImageHeight;
        const distance = Math.hypot(mouseX - groupX, mouseY - groupY);

        if (distance <= radius) {
          const localDate = new Date(alarmInfoSelected?.dateTime);
          const dateTime = localDate.toLocaleString();
          tooltipStr += `<div>${localizationService.toLanguageString('map.name', 'NOT_FOUND')}: ${alarmChecked.name}</div><div>${localizationService.toLanguageString('map.date_time', 'NOT_FOUND')}: ${dateTime}</div>`;
          found = true;
        }
      }
    }

    if (found) {
      setTooltipContent(tooltipStr);
      if (isDeviceTooltip) {
        // Anchor beside the pointer, flip to the left near the viewport edge and clamp vertically.
        const estimatedWidth = 340;
        const estimatedHalfHeight = 110;
        const flip = e.clientX + 24 + estimatedWidth > window.innerWidth;
        const clampedY = Math.min(
          Math.max(e.clientY, estimatedHalfHeight + 8),
          window.innerHeight - estimatedHalfHeight - 8
        );
        setTooltipVariant(flip ? 'device-flipped' : 'device');
        setTooltipPos({ x: flip ? e.clientX - 20 : e.clientX + 20, y: clampedY });
      } else {
        const yOffset = isBeaconTooltip ? -15 : -35;
        setTooltipVariant('default');
        setTooltipPos({ x: e.clientX + 10, y: e.clientY + yOffset });
      }
      setShowTooltip(true);
    } else {
      setShowTooltip(false);
      setTooltipPos({ x: 0, y: 0 });
      setTooltipContent('');
    }
  };

  const mapNotificationGroupStyle = {
    right: 0,
    bottom: 50,
    alignItems: 'flex-start' as const,
    flexWrap: 'wrap-reverse' as const
  };

  const mapsTransformWrapperStyle = {
    width: '100%',
    height: '100%'
  };

  const hasCheckedBeacons = Array.isArray(selectedBeacons) && selectedBeacons.length > 0;

  return (

    <div
      className="flex-parent-element-maps maps-layout-root"
    >
      <NotificationGroup
        style={mapNotificationGroupStyle}
      >
        <Fade enter={true} exit={true}>
          {noPositionFound && (
            <Notification
              type={{ style: "warning", icon: true }}
              closable={true}
              onClose={() => setNoPositionFound(false)}
            >
              <span>{localizationService.toLanguageString('map.no_position_found', 'NOT_FOUND')}</span>
            </Notification>
          )}
        </Fade>
      </NotificationGroup>

      {loading && loadingPanel}

      <div
        className="flex-left-child-element canvas-container maps-canvas-pane"
      >
        <div className="maps-canvas-surface">
          <div
            ref={mapRef}
            className={`maps-map-pane maps-map-pane-static ${mapGeoreferenced ? 'maps-hidden' : ''}`}
          >

            <TransformWrapper
              key={`transform-${mapName}-${initialScale}`}
              initialScale={initialScale}
              initialPositionX={0}
              initialPositionY={0}
              minScale={0.1}
              centerOnInit={true}
              centerZoomedOut={true}
              smooth={true}
              onTransformed={(_, state) => {
                setTransformState(state);
                if (lastMouseEvent) {
                  handleUnifiedTooltip(lastMouseEvent);
                }
              }}
            >
              {() => (
                <>
                  <MapsControls localizationService={localizationService} />
                  {noMaps &&
                    // Mostra un messaggio o un altro contenuto se non ci sono dati
                    <div className="maps-empty-state">
                      <p>{localizationService.toLanguageString('map.no_map', 'NOT_FOUND')}</p>
                    </div>
                  }
                  {showTooltip && (
                    <div
                      ref={tooltipRef}
                      className={tooltipVariant === 'default'
                        ? 'maps-tooltip'
                        : `maps-tooltip maps-tooltip--device${tooltipVariant === 'device-flipped' ? ' maps-tooltip--flipped' : ''}`}
                      style={tooltipVariant === 'default'
                        ? {
                          top: tooltipPos.y - 30,
                          left: tooltipPos.x + 35,
                          // Center tooltip anchor relative to pointer while preserving dynamic coordinates.
                        }
                        : { top: tooltipPos.y, left: tooltipPos.x }}
                      dangerouslySetInnerHTML={{ __html: tooltipContent }}
                    />
                  )}

                  <TransformComponent
                    wrapperStyle={mapsTransformWrapperStyle}
                    contentStyle={{
                      width: mapImageWidth,
                      height: mapImageHeight,
                      position: 'relative'
                    }}
                  >
                    <div ref={transformContentRef} onMouseMove={handleUnifiedTooltip}>
                      {mapImageWidth > 0 && mapImageHeight > 0 && (
                        <>
                          <canvas
                            ref={mapCanvas}
                            className="mapCanvas"
                            width={mapImageWidth}
                            height={mapImageHeight}
                          />
                          <canvas
                            ref={zoneCanvas}
                            className="zoneCanvas"
                            width={mapImageWidth}
                            height={mapImageHeight}
                          />
                          <canvas
                            ref={alarmCanvas}
                            className="alarmCanvas"
                            width={mapImageWidth}
                            height={mapImageHeight}
                          />

                          {/* SVG layer for high-quality group markers */}
                          <svg
                            className="groupCanvasSVG maps-svg-layer maps-svg-layer-group"
                            width={mapImageWidth}
                            height={mapImageHeight}
                          >
                            {groupMarkersSVG.map((marker: any, idx: number) => {
                              // Usa dimensioni fisse e applica scale tramite transform
                              const dropRadius = marker?.markerRadius;
                              const centerY = marker?.y;
                              const bottomY = marker?.tipY;
                              const topY = centerY - dropRadius * 0.7;
                              const markerScale = marker?.scale || 1;
                              const markerOffset = markerOffsets[normalizeDeviceMac(marker?.deviceMac)];
                              const offsetTransform = markerOffset ? `translate(${markerOffset.dx}, ${markerOffset.dy}) ` : '';

                              return (
                                <g key={`marker-${idx}`} transform={`${offsetTransform}translate(${marker.x}, ${marker.tipY}) scale(${markerScale}) translate(${-marker.x}, ${-marker.tipY})`}>
                                  {/* Forma a goccia colorata con dimensioni fisse */}
                                  <path
                                    d={`
                                    M ${marker?.x} ${bottomY}
                                    C ${marker?.x - dropRadius * 0.4} ${bottomY - dropRadius * 1.2},
                                      ${marker?.x - dropRadius * 0.85} ${centerY - dropRadius * 0.2},
                                      ${marker?.x - dropRadius * 0.85} ${topY}
                                    C ${marker?.x - dropRadius * 0.85} ${topY - dropRadius * 0.3},
                                      ${marker?.x - dropRadius * 0.5} ${topY - dropRadius * 0.5},
                                      ${marker?.x} ${topY - dropRadius * 0.5}
                                    C ${marker?.x + dropRadius * 0.5} ${topY - dropRadius * 0.5},
                                      ${marker?.x + dropRadius * 0.85} ${topY - dropRadius * 0.3},
                                      ${marker?.x + dropRadius * 0.85} ${topY}
                                    C ${marker?.x + dropRadius * 0.85} ${centerY - dropRadius * 0.2},
                                      ${marker?.x + dropRadius * 0.4} ${bottomY - dropRadius * 1.2},
                                      ${marker?.x} ${bottomY}
                                    Z
                                  `}
                                    fill={marker?.color}
                                    stroke="black"
                                    strokeWidth="1"
                                  />
                                </g>
                              );
                            })}
                          </svg>

                          {/* SVG layer for beacon markers */}
                          <svg
                            className="beaconCanvasSVG maps-svg-layer maps-svg-layer-beacon"
                            width={mapImageWidth}
                            height={mapImageHeight}
                          >
                            {beaconMarkersSVG.map((beacon: any, idx: number) => {
                              const beaconWidth = 30;
                              const beaconHeight = 18;
                              const beaconScale = beacon.scale || 1;

                              return (
                                <g key={`beacon-${idx}`} transform={`translate(${beacon.x}, ${beacon.y}) scale(${beaconScale}) translate(${-beacon.x}, ${-beacon.y})`}>
                                  <rect
                                    x={beacon.x - beaconWidth / 2}
                                    y={beacon.y - beaconHeight / 2}
                                    width={beaconWidth}
                                    height={beaconHeight}
                                    fill={beacon.color}
                                    stroke="black"
                                    strokeWidth="0.5"
                                  />
                                </g>
                              );
                            })}
                          </svg>
                        </>
                      )}
                    </div>
                  </TransformComponent>
                </>
              )}
            </TransformWrapper>
          </div>
          <div
            ref={mapRef}
            className={`maps-map-pane ${mapGeoreferenced ? '' : 'maps-hidden'}`}
          >
            {listMaps.length != 0 && mapGeoreferenced &&
              <AzureMapComponent
                map={selectedMap ?? null}
                zones={safeJsonParse<any[]>(localStorage.getItem('zonesChecked'), [])}
                devices={Array.isArray(deviceChecked) ? deviceChecked : []}
                beacons={Array.isArray(listBeaconsOn) ? listBeaconsOn : []}
                groupingEnabled={groupingEnabled}
                deviceMac={deviceMacSelected}
                groupChanged={groupChanged}
                onlyBound={onlyBound}
                alarmInfo={alarmInfo}
                alarmToggle={alarmToggle}
                geoStaticToggle={toggleGeoStatic}
              />
            }
          </div>
        </div>
      </div>

      <div className="flex-right-child-element-maps maps-control-pane" >
        <div className="maps-control-header">
          <h3 className="maps-control-title">{localizationService.toLanguageString('map.map_list', 'NOT_FOUND')}</h3>
        </div>
        <div className="maps-panel-content">
          <div className="maps-control-section">
            <DropDownList
              data={listMapNames}
              value={mapName.length > 0 ? mapName : undefined}
              defaultItem={localizationService.toLanguageString('map.select_map', 'NOT_FOUND')}
              onChange={async (e: any) => {
                setDeviceMacSelected('')
                localStorage.removeItem('deviceMapId')
                localStorage.removeItem('deviceGroupId');
                localStorage.removeItem('mapGeoreferenced');

                // @ts-ignore
                if (listMapNames.includes(e.value)) {
                  localStorage.removeItem('alarmInfoSelected');
                  localStorage.removeItem('beaconFromGrid');
                  await changeMap(e, true)
                }
              }}
              className="maps-select"
              title={localizationService.toLanguageString('map.select_map', 'NOT_FOUND')}
            />
          </div>

          <div className="maps-control-section">
            <div className="maps-switch-row k-mb-2">
              <Switch
                onChange={onToggleLive}
                disabled={(mapId != '' && (deviceMacFromSynoptic == null ? false : true) && deviceMapIdFromSynoptic == mapId)}
                checked={liveStatusMap}
              />
              <label style={{ margin: '0',  }} >{localizationService.toLanguageString('map.live', 'NOT_FOUND')}</label>
            </div>

            <div className="maps-switch-row k-mb-2">
              <Switch
                onChange={onToggleOnlyBound}
                checked={onlyBound}
                disabled={((deviceMacFromSynoptic != null ? true : false) && deviceMapIdFromSynoptic == mapId)}
              />
              <label style={{ margin: '0' }} >{localizationService.toLanguageString('synoptic_devices.only_binded', 'NOT_FOUND')}</label>
            </div>

            {selectedMap?.georeferenced == 1 && selectedMap?.image != '' &&
              <div className="maps-switch-row k-mb-2">
                <Switch
                  onChange={onToggleGeoStatic}
                  checked={toggleGeoStatic}
                />
                <label style={{ margin: '0' }}>
                  {toggleGeoStatic ?
                    localizationService.toLanguageString('map.geo', 'NOT_FOUND')
                    :
                    localizationService.toLanguageString('map.static', 'NOT_FOUND')
                  }
                </label>
              </div>
            }

            {selectedMap?.georeferenced == 1 && mapGeoreferenced &&
              <div className="maps-switch-row k-mb-2">
                <Switch
                  onChange={onToggleGrouping}
                  checked={groupingEnabled}
                />
                <label style={{ margin: '0' }} >
                  {localizationService.toLanguageString('map.grouping', 'NOT_FOUND')}
                </label>
              </div>
            }
            
          </div>

          <div className={`maps-control-section maps-filter-pill ${showDeviceMacBySynoptic ? '' : 'maps-hidden'}`}>
            <div
              className="trash-icon maps-filter-clear"
              title={localizationService.toLanguageString('map.remove_filter', 'NOT_FOUND')}
              onClick={() => {
                setShowDeviceMacBySynoptic(false);
                setDeviceMacSelected('');
                localStorage.removeItem('deviceMac');
                localStorage.removeItem('mapSelected');
                localStorage.removeItem('deviceMacFromSynoptic');
                localStorage.removeItem('deviceMapIdFromSynoptic');
                refreshGroupPositionsInMap(mapId, mapGeoreferenced);
              }}
            >
              <Avatar
                className="trash-icon"
              >
                <SvgIcon
                  icon={trashIcon}
                  className="maps-filter-clear-icon"
                />
              </Avatar>
            </div>
            <p className="maps-filter-label">
              <label className="k-mb-2">Mac: {deviceMacFromSynoptic}</label>
            </p>
          </div>

          <div className={`maps-control-section maps-filter-pill ${showBeaconMac ? '' : 'maps-hidden'}`}>
            <div
              className="trash-icon maps-filter-clear"
              title={localizationService.toLanguageString('map.remove_filter', 'NOT_FOUND')}
              onClick={() => {
                setShowBeaconMac(false);
                changeCheckboxBeacon(true, beaconIdSelected);
                setBeaconIdSelected('');
                onChangeBeacon({});
                localStorage.removeItem('deviceMac');
                localStorage.removeItem('mapSelected');
                localStorage.removeItem('mapIdByBeacon');
                localStorage.removeItem('beaconFromGrid');
              }}
            >
              <Avatar
                className="trash-icon"
              >
                <SvgIcon
                  icon={trashIcon}
                  className="maps-filter-clear-icon"
                />
              </Avatar>
            </div>
            <p className="maps-filter-label">
              <label className="k-mb-2">Mac: {beaconIdSelected}</label>
            </p>
          </div>

          {/*zones checkboxes*/}
          <div className="maps-control-section">
            <ExpansionPanel
            className="maps-group-panel"
              title={localizationService.toLanguageString('map.zone_list', 'NOT_FOUND')}
              expanded={zonesPanelExpanded}
              onAction={() => setZonesPanelExpanded(!zonesPanelExpanded)}
            >
              {zonesPanelExpanded && (
                <ExpansionPanelContent className="maps-group-panel">
                  <div className="maps-checklist">
                    {zonesCheckboxByMap.map((zone: any, index: any) => {
                      return (
                        <div key={`zone-${zone?.id ?? 'na'}-${index}`}>
                          <Checkbox
                            name={zone?.name ?? zone}
                            onChange={(e: any) => changeZone(e, zone?.id)}
                            label={zone.name ?? zone}
                            checked={zone.checked ?? false}
                          >
                            <label className="checkbox-label-zone maps-marker-chip" style={markerColorStyle(zone?.color)} />
                          </Checkbox>
                        </div>
                      );
                    })}
                  </div>
                </ExpansionPanelContent>
              )}
            </ExpansionPanel>
          </div>

          {/*groups checkboxes*/}
          <div className="maps-control-section">
            <ExpansionPanel
              className="maps-group-panel"
              title={localizationService.toLanguageString('map.group_list', 'NOT_FOUND')}
              expanded={groupsPanelExpanded}
              onAction={() => setGroupsPanelExpanded(!groupsPanelExpanded)}
            >
              {groupsPanelExpanded && (
                <ExpansionPanelContent>
                  {
                    !mapGeoreferenced ?
                      groupsCheckboxByMap?.map((group: any, index: any) => {
                        return (
                          <div key={`group-static-${group?.id ?? 'na'}-${index}`}>
                            <Checkbox
                              name={group?.name ?? group}
                              value={group?.name ?? group}
                              onChange={(e: any) => changeGroup(e, group?.id, false)}
                              label={group.name ?? group}
                              checked={group.checked}
                              disabled={deviceMacFromSynoptic != null ? true : false}
                            >
                              <label className="checkbox-label-group maps-marker-chip" style={markerColorStyle(group?.color)} />
                            </Checkbox>
                          </div>
                        );
                      })
                      :
                      groupsCheckboxByMap?.map((group: any, index: any) => {
                        return (
                          <div key={`group-geo-${group?.id ?? 'na'}-${index}`}>
                            <Checkbox
                              name={group?.name ?? group}
                              value={group?.name ?? group}
                              onChange={(e: any) => changeGroup(e, group?.id, true)}
                              label={group?.name ?? ''}
                              checked={group?.checked}
                            >
                              <label className="checkbox-label-group maps-marker-chip" style={markerColorStyle(group?.color)} />
                            </Checkbox>
                          </div>
                        );
                      })
                  }
                </ExpansionPanelContent>
              )}
            </ExpansionPanel>
          </div>

          {/*beacons checkboxes*/}
          <div className={`maps-control-section maps-beacons-section ${hasCheckedBeacons ? 'maps-beacons-section-expanded' : ''}`}>
            <ExpansionPanel
              className="maps-group-panel"
              title={localizationService.toLanguageString('beacons.title', 'NOT_FOUND')}
              expanded={beaconsPanelExpanded}
              onAction={() => setBeaconsPanelExpanded(!beaconsPanelExpanded)}
            >
              {beaconsPanelExpanded && (
                <ExpansionPanelContent>
                  <MultiSelect
                    className="maps-beacon-multiselect"
                    data={listBeaconsOn}
                    textField="id"
                    dataItemKey="id"
                    //value={JSON.parse(localStorage.getItem('beaconFromGrid') as string) != null ? JSON.parse(localStorage.getItem('beaconFromGrid') as string) : selectedBeacons}
                    value={selectedBeacons}
                    onChange={(e: any) => onChangeBeacon(e)}
                    itemRender={beaconsRenderList}
                    autoClose={false}
                    filterable={true}
                    onFilterChange={filterChange2}
                    onFocus={filterChange2}
                    tagRender={tagRender}
                    disabled={showBeaconMac}
                    placeholder={localizationService.toLanguageString('beacons.select_beacon', 'NOT_FOUND')}
                  />
                </ExpansionPanelContent>
              )}
            </ExpansionPanel>
          </div>

          {alarmChecked &&
            <div className="maps-control-section">
              <h4 className="k-h4">{localizationService.toLanguageString('map.alarm', 'NOT_FOUND')}</h4>
              <Checkbox
                name={alarmChecked?.name}
                value={alarmToggle ?? undefined}
                onChange={(e: any) => {
                  changeAlarm(e)
                  setAlarmInfo(alarmChecked)
                }}
                label={alarmChecked?.name}
              >
                <label className="checkbox-label-beacon maps-alarm-indicator" />
              </Checkbox>
            </div>
          }
        </div>
      </div>
    </div>
  )
}
